// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  pageLead:
    'How good work gets done here. Poneva finds the Skill that fits, in any chat or agent, and follows it on top of your company knowledge.',
  search: 'Find Skills by meaning…',
  pageNote: 'Write and change Skills in chat. Poneva follows published Skills wherever they fit.',
  cardApplies: 'Applies when it fits',
  cardDraft: 'Draft · Not published',
  cardNewDraft: 'New draft',
  cardVersion: 'v{revision}',
  emptyTitle: 'Start with something you do often.',
  emptyBody:
    'Tell Poneva how a kind of work is done here. It writes the instructions with you, on top of your company knowledge, and every chat and agent follows them once you publish.',
  emptyAction: 'Create your first Skill',
  draftCard: 'Draft {revision} · Open instructions',
  versionPublished: 'Version {revision} · Published',
  versionDraft: 'Version {revision} · Draft',
  versionEarlier: 'Version {revision} · Earlier',
  publishedVersion: 'Version {revision} is the published version.',
  useInChat: 'Use in chat',
  editInChat: 'Edit in chat',
  editLatest: 'Edit latest in chat',
  applies: 'Applies',
  appliesWhenItFits: 'When it fits',
  appliesHint: 'Poneva finds it by its Use when line, in any chat or agent run.',
  filesRow: 'Files',
  tabInstructions: 'Instructions',
  tabFollowedIn: 'Followed in',
  tabVersions: 'Versions',
  followedInAnswersOne: 'Followed in {count} answer this week.',
  followedInAnswersOther: 'Followed in {count} answers this week.',
  followedInEmpty:
    'Not followed yet. When a chat or an agent run follows this Skill, it shows here.',
  followedInFailed: 'Where this Skill was followed could not be loaded.',
  versionsCurrent: 'Current',
  versionsView: 'View',
  restore: 'Restore as new draft',
  restored: 'Earlier instructions restored as a new draft.',
  restoreFailed: 'This version could not be restored.',
  publishedFor: 'Published for {company}.',
  editStaysDraft: 'Changes you make in chat stay a draft until you publish them.',
  draftOnlyYou: 'Only you can see this draft.',
  draftShared: 'Only you and the people it is shared with can see this draft.',
  publishHint: 'Publish when it reads right. Nothing has to be tried first.',
  newerDraftHint: 'Publish this version when it reads right. Nothing has to be tried first.',
  publish: 'Publish',
  requestPublication: 'Request publication',
  publishing: 'Publishing…',
  published: 'Published for your company.',
  requested: 'Sent for approval. A publisher reviews this version in their Inbox.',
  publishFailed: 'This version could not be published. Try again.',
  companyFile: 'Company file',
  discussPassage: 'Discuss this passage',
  contentLabel: 'Skill content',
  unpublish: 'Unpublish company version',
  unpublishQuestion:
    'Unpublish the company version? Colleagues will lose access to its published instructions, and chats and agents stop following it.',
  confirmUnpublish: 'Confirm unpublish',
  unpublishing: 'Unpublishing…',
  cancel: 'Cancel',
  unpublished: 'Company version unpublished. Drafts are unchanged.',
  unpublishFailed: 'Could not unpublish this Skill.',
  newSkillTitle: 'What should this Skill teach?',
  newSkillBody:
    'Describe how the work is done here. Poneva drafts the instructions on this side, from your company knowledge.',
  followedOne: 'Followed {count} Skill',
  followedMany: 'Followed {count} Skills',
  followedSubtitle: 'Read before this answer',
  howFound: 'Found for “{need}”',
  howFoundPlain: 'Found for this request',
  howPicked: 'Picked for this chat',
  howAgent: 'Always followed by this agent',
  howRead: 'Read for this answer',
  openFollowed: 'Open {name}, version {revision}',
  followedEyebrow: 'Skill · Version {revision}',
  followedContext: 'Followed for this answer · Version {revision}',
  followedRecordNote:
    'This records what Poneva was given before writing this answer, not a check that every step was followed.',
  openInSkills: 'Open in Skills',
  newerVersion: 'A newer version exists (Version {revision}).',
  viewNewer: 'View version {revision}',
  versionUnavailable:
    'Version {revision} is no longer available to you. This is the current version.',
  versionLoadFailed:
    'Could not load version {revision}. Try again to see the instructions used for this answer.',
  retryVersion: 'Try again',
  lookedFor: 'Looked for',
  searched: 'Searched',
  searchedSkills: 'Company Skills, by their Use when line',
  found: 'Found',
  bestFit: 'Best fit',
  noneFound: 'No Skill matched this search.',
  skillLabel: 'Skill',
  versionLabel: 'Version {revision}',
};

export type SkillsCopyKey = keyof typeof en;
export type SkillsCopy = Record<SkillsCopyKey, string>;

const copy: NamespaceCatalog<keyof typeof en> = {
  bg: {
    pageLead:
      'Така се върши добра работа тук. Poneva намира подходящото Умение във всеки чат или агент и го следва върху базата от знания на компанията ви.',
    search: 'Намиране на Умения по смисъл…',
    pageNote:
      'Пишете и променяйте Уменията в чат. Poneva следва публикуваните Умения навсякъде, където са подходящи.',
    cardApplies: 'Прилага се, когато е подходящо',
    cardDraft: 'Чернова · Непубликувано',
    cardNewDraft: 'Нова чернова',
    cardVersion: 'v{revision}',
    emptyTitle: 'Започнете с нещо, което правите често.',
    emptyBody:
      'Кажете на Poneva как се върши определен вид работа тук. Тя ще напише инструкциите заедно с вас, върху базата от знания на компанията ви, а всеки чат и агент ще ги следва, след като ги публикувате.',
    emptyAction: 'Създайте първото си Умение',
    draftCard: 'Чернова {revision} · Отваряне на инструкциите',
    versionPublished: 'Версия {revision} · Публикувана',
    versionDraft: 'Версия {revision} · Чернова',
    versionEarlier: 'Версия {revision} · По-стара',
    publishedVersion: 'Версия {revision} е публикуваната версия.',
    useInChat: 'Използване в чат',
    editInChat: 'Редактиране в чат',
    editLatest: 'Редактиране на последната версия в чат',
    applies: 'Прилага се',
    appliesWhenItFits: 'Когато е подходящо',
    appliesHint:
      'Poneva го намира по реда „Използвайте, когато…“ във всеки чат или изпълнение на агент.',
    filesRow: 'Файлове',
    tabInstructions: 'Инструкции',
    tabFollowedIn: 'Следвано в',
    tabVersions: 'Версии',
    followedInAnswersOne: 'Следвано в {count} отговор тази седмица.',
    followedInAnswersOther: 'Следвано в {count} отговора тази седмица.',
    followedInEmpty:
      'Все още не е следвано. Когато чат или изпълнение на агент следва това Умение, то ще се покаже тук.',
    followedInFailed: 'Местата, на които е следвано това Умение, не могат да бъдат заредени.',
    versionsCurrent: 'Текуща',
    versionsView: 'Преглед',
    restore: 'Възстановяване като нова чернова',
    restored: 'По-старите инструкции са възстановени като нова чернова.',
    restoreFailed: 'Тази версия не може да бъде възстановена.',
    publishedFor: 'Публикувано за {company}.',
    editStaysDraft: 'Промените, които правите в чат, остават чернова, докато не ги публикувате.',
    draftOnlyYou: 'Само вие можете да виждате тази чернова.',
    draftShared: 'Само вие и хората, с които е споделена, можете да виждате тази чернова.',
    publishHint:
      'Публикувайте, когато текстът ви се струва точен. Не е нужно първо да го изпробвате.',
    newerDraftHint:
      'Публикувайте тази версия, когато текстът ви се струва точен. Не е нужно първо да я изпробвате.',
    publish: 'Публикуване',
    requestPublication: 'Заявяване на публикуване',
    publishing: 'Публикуване…',
    published: 'Публикувано за компанията ви.',
    requested:
      'Изпратено за одобрение. Публикуващ отговорник ще прегледа тази версия във входящата си кутия.',
    publishFailed: 'Тази версия не може да бъде публикувана. Опитайте отново.',
    companyFile: 'Файл на компанията',
    discussPassage: 'Обсъждане на този откъс',
    contentLabel: 'Съдържание на Умението',
    unpublish: 'Премахване на фирмената версия',
    unpublishQuestion:
      'Да се премахне ли фирмената версия от публикуване? Колегите ще загубят достъп до публикуваните инструкции, а чатовете и агентите ще спрат да ги следват.',
    confirmUnpublish: 'Потвърждаване на премахването от публикуване',
    unpublishing: 'Премахване от публикуване…',
    cancel: 'Отказ',
    unpublished: 'Фирмената версия е премахната от публикуване. Черновите не са променени.',
    unpublishFailed: 'Това Умение не може да бъде премахнато от публикуване.',
    newSkillTitle: 'На какво трябва да учи това Умение?',
    newSkillBody:
      'Опишете как се върши работата тук. Poneva ще създаде чернова на инструкциите от тази страна, въз основа на знанията на компанията ви.',
    followedOne: 'Следвано Умение: {count}',
    followedMany: 'Следвани Умения: {count}',
    followedSubtitle: 'Прочетено преди този отговор',
    howFound: 'Намерено за „{need}“',
    howFoundPlain: 'Намерено за тази заявка',
    howPicked: 'Избрано за този чат',
    howAgent: 'Винаги следвано от този агент',
    howRead: 'Прочетено за този отговор',
    openFollowed: 'Отваряне на {name}, версия {revision}',
    followedEyebrow: 'Умение · Версия {revision}',
    followedContext: 'Следвано за този отговор · Версия {revision}',
    followedRecordNote:
      'Това показва какво е било предоставено на Poneva, преди да напише този отговор, а не потвърждава, че всяка стъпка е била следвана.',
    openInSkills: 'Отваряне в Умения',
    newerVersion: 'Има по-нова версия (версия {revision}).',
    viewNewer: 'Преглед на версия {revision}',
    versionUnavailable: 'Версия {revision} вече не е достъпна за вас. Това е текущата версия.',
    versionLoadFailed:
      'Версия {revision} не може да бъде заредена. Опитайте отново, за да видите инструкциите, използвани за този отговор.',
    retryVersion: 'Опитайте отново',
    lookedFor: 'Търсено',
    searched: 'Претърсено',
    searchedSkills: 'Фирмени умения според реда „Кога да се използва“',
    found: 'Намерени',
    bestFit: 'Най-подходящо',
    noneFound: 'Няма умение, съвпадащо с това търсене.',
    skillLabel: 'Умение',
    versionLabel: 'Версия {revision}',
  },
  hr: {
    pageLead:
      'Ovako se ovdje obavlja kvalitetan posao. Poneva pronalazi Vještinu koja odgovara, u bilo kojem chatu ili agentu, i primjenjuje je povrh znanja vaše tvrtke.',
    search: 'Pronađite Vještine prema značenju…',
    pageNote:
      'Pišite i mijenjajte Vještine u chatu. Poneva primjenjuje objavljene Vještine gdje god odgovaraju.',
    cardApplies: 'Primjenjuje se kada odgovara',
    cardDraft: 'Skica · Nije objavljeno',
    cardNewDraft: 'Nova skica',
    cardVersion: 'v{revision}',
    emptyTitle: 'Započnite nečim što često radite.',
    emptyBody:
      'Recite Ponevi kako se ovdje obavlja određena vrsta posla. Ona s vama piše upute na temelju znanja vaše tvrtke, a svaki chat i agent primjenjivat će ih nakon objave.',
    emptyAction: 'Izradite svoju prvu Vještinu',
    draftCard: 'Skica {revision} · Otvori upute',
    versionPublished: 'Verzija {revision} · Objavljeno',
    versionDraft: 'Verzija {revision} · Skica',
    versionEarlier: 'Verzija {revision} · Prethodna',
    publishedVersion: 'Verzija {revision} objavljena je verzija.',
    useInChat: 'Koristi u chatu',
    editInChat: 'Uredi u chatu',
    editLatest: 'Uredi najnoviju u chatu',
    applies: 'Primjenjuje se',
    appliesWhenItFits: 'Kada odgovara',
    appliesHint:
      'Poneva je pronalazi prema retku „Koristi kada”, u bilo kojem chatu ili pokretanju agenta.',
    filesRow: 'Datoteke',
    tabInstructions: 'Upute',
    tabFollowedIn: 'Primijenjeno u',
    tabVersions: 'Verzije',
    followedInAnswersOne: 'Primijenjeno u {count} odgovoru ovaj tjedan.',
    followedInAnswersOther: 'Primijenjeno u {count} odgovora ovaj tjedan.',
    followedInEmpty:
      'Još nije primijenjeno. Kada chat ili pokretanje agenta primijeni ovu Vještinu, prikazat će se ovdje.',
    followedInFailed: 'Nije moguće učitati gdje je ova Vještina primijenjena.',
    versionsCurrent: 'Trenutna',
    versionsView: 'Prikaži',
    restore: 'Vrati kao novu skicu',
    restored: 'Prethodne upute vraćene su kao nova skica.',
    restoreFailed: 'Ovu verziju nije moguće vratiti.',
    publishedFor: 'Objavljeno za {company}.',
    editStaysDraft: 'Promjene koje napravite u chatu ostaju skica dok ih ne objavite.',
    draftOnlyYou: 'Samo vi možete vidjeti ovu skicu.',
    draftShared: 'Samo vi i osobe s kojima je skica podijeljena možete je vidjeti.',
    publishHint: 'Objavite kada sadržaj bude odgovarajući. Ništa se prethodno ne mora isprobati.',
    newerDraftHint:
      'Objavite ovu verziju kada sadržaj bude odgovarajući. Ništa se prethodno ne mora isprobati.',
    publish: 'Objavi',
    requestPublication: 'Zatraži objavu',
    publishing: 'Objavljivanje…',
    published: 'Objavljeno za vašu tvrtku.',
    requested: 'Poslano na odobrenje. Objavljivač pregledava ovu verziju u svom Inboxu.',
    publishFailed: 'Ovu verziju nije moguće objaviti. Pokušajte ponovno.',
    companyFile: 'Datoteka tvrtke',
    discussPassage: 'Raspravi o ovom odlomku',
    contentLabel: 'Sadržaj Vještine',
    unpublish: 'Poništi objavu verzije tvrtke',
    unpublishQuestion:
      'Poništiti objavu verzije tvrtke? Kolege će izgubiti pristup njezinim objavljenim uputama, a chatovi i agenti prestat će je primjenjivati.',
    confirmUnpublish: 'Potvrdi poništavanje objave',
    unpublishing: 'Poništavanje objave…',
    cancel: 'Odustani',
    unpublished: 'Objava verzije tvrtke poništena je. Skice nisu promijenjene.',
    unpublishFailed: 'Nije moguće poništiti objavu ove Vještine.',
    newSkillTitle: 'Čemu bi ova Vještina trebala podučavati?',
    newSkillBody:
      'Opišite kako se ovdje obavlja posao. Poneva na ovoj strani izrađuje skicu uputa na temelju znanja vaše tvrtke.',
    followedOne: 'Primijenjena Vještina: {count}',
    followedMany: 'Primijenjene Vještine: {count}',
    followedSubtitle: 'Pročitano prije ovog odgovora',
    howFound: 'Pronađeno za „{need}”',
    howFoundPlain: 'Pronađeno za ovaj zahtjev',
    howPicked: 'Odabrano za ovaj chat',
    howAgent: 'Ovaj agent uvijek primjenjuje',
    howRead: 'Pročitano za ovaj odgovor',
    openFollowed: 'Otvori {name}, verzija {revision}',
    followedEyebrow: 'Vještina · Verzija {revision}',
    followedContext: 'Primijenjeno za ovaj odgovor · Verzija {revision}',
    followedRecordNote:
      'Ovo bilježi što je Poneva dobila prije pisanja ovog odgovora, a ne provjeru je li svaki korak proveden.',
    openInSkills: 'Otvori u Vještinama',
    newerVersion: 'Dostupna je novija verzija (verzija {revision}).',
    viewNewer: 'Prikaži verziju {revision}',
    versionUnavailable: 'Verzija {revision} više vam nije dostupna. Ovo je trenutačna verzija.',
    versionLoadFailed:
      'Nije moguće učitati verziju {revision}. Pokušajte ponovno kako biste vidjeli upute korištene za ovaj odgovor.',
    retryVersion: 'Pokušaj ponovno',
    lookedFor: 'Traženo',
    searched: 'Pretraženo',
    searchedSkills: 'Vještine tvrtke prema retku „Kada koristiti”',
    found: 'Pronađeno',
    bestFit: 'Najbolje podudaranje',
    noneFound: 'Nijedna vještina ne odgovara ovoj pretrazi.',
    skillLabel: 'Vještina',
    versionLabel: 'Verzija {revision}',
  },
  cs: {
    pageLead:
      'Takto se tu odvádí kvalitní práce. Poneva najde dovednost, která se hodí, v jakémkoli chatu nebo agentovi, a použije ji spolu se znalostmi vaší společnosti.',
    search: 'Hledat dovednosti podle významu…',
    pageNote:
      'Pište a upravujte dovednosti v chatu. Poneva používá publikované dovednosti všude, kde se hodí.',
    cardApplies: 'Použije se, když se hodí',
    cardDraft: 'Koncept · Nepublikováno',
    cardNewDraft: 'Nový koncept',
    cardVersion: 'v{revision}',
    emptyTitle: 'Začněte něčím, co děláte často.',
    emptyBody:
      'Řekněte Ponevě, jak se tu určitý druh práce dělá. Společně s vámi sepíše pokyny na základě znalostí vaší společnosti a každý chat i agent se jimi po publikování bude řídit.',
    emptyAction: 'Vytvořit první dovednost',
    draftCard: 'Koncept {revision} · Otevřít pokyny',
    versionPublished: 'Verze {revision} · Publikováno',
    versionDraft: 'Verze {revision} · Koncept',
    versionEarlier: 'Verze {revision} · Starší',
    publishedVersion: 'Verze {revision} je publikovaná verze.',
    useInChat: 'Použít v chatu',
    editInChat: 'Upravit v chatu',
    editLatest: 'Upravit nejnovější verzi v chatu',
    applies: 'Použije se',
    appliesWhenItFits: 'Když se hodí',
    appliesHint: 'Poneva ji najde podle řádku „Použít, když…“ v jakémkoli chatu nebo běhu agenta.',
    filesRow: 'Soubory',
    tabInstructions: 'Pokyny',
    tabFollowedIn: 'Použito v',
    tabVersions: 'Verze',
    followedInAnswersOne: 'Použito v {count} odpovědi tento týden.',
    followedInAnswersOther: 'Použito v {count} odpovědích tento týden.',
    followedInEmpty:
      'Zatím nebylo použito. Jakmile se touto dovedností bude řídit chat nebo běh agenta, zobrazí se zde.',
    followedInFailed: 'Nepodařilo se načíst, kde byla tato dovednost použita.',
    versionsCurrent: 'Aktuální',
    versionsView: 'Zobrazit',
    restore: 'Obnovit jako nový koncept',
    restored: 'Starší pokyny byly obnoveny jako nový koncept.',
    restoreFailed: 'Tuto verzi se nepodařilo obnovit.',
    publishedFor: 'Publikováno pro společnost {company}.',
    editStaysDraft: 'Změny provedené v chatu zůstanou konceptem, dokud je nepublikujete.',
    draftOnlyYou: 'Tento koncept vidíte jen vy.',
    draftShared: 'Tento koncept vidíte jen vy a lidé, se kterými je sdílený.',
    publishHint: 'Publikujte, až bude text správně formulovaný. Předtím není nutné nic zkoušet.',
    newerDraftHint:
      'Publikujte tuto verzi, až bude text správně formulovaný. Předtím není nutné nic zkoušet.',
    publish: 'Publikovat',
    requestPublication: 'Požádat o publikování',
    publishing: 'Publikuje se…',
    published: 'Publikováno pro vaši společnost.',
    requested: 'Odesláno ke schválení. Vydavatel tuto verzi zkontroluje ve své doručené poště.',
    publishFailed: 'Tuto verzi se nepodařilo publikovat. Zkuste to znovu.',
    companyFile: 'Firemní soubor',
    discussPassage: 'Diskutovat o této pasáži',
    contentLabel: 'Obsah dovednosti',
    unpublish: 'Zrušit publikování firemní verze',
    unpublishQuestion:
      'Zrušit publikování firemní verze? Kolegové ztratí přístup k jejím publikovaným pokynům a chaty i agenti se jimi přestanou řídit.',
    confirmUnpublish: 'Potvrdit zrušení publikování',
    unpublishing: 'Publikování se ruší…',
    cancel: 'Zrušit',
    unpublished: 'Publikování firemní verze bylo zrušeno. Koncepty zůstaly beze změny.',
    unpublishFailed: 'Publikování této dovednosti se nepodařilo zrušit.',
    newSkillTitle: 'Co má tato dovednost učit?',
    newSkillBody:
      'Popište, jak se tu práce dělá. Poneva na této straně sepíše pokyny na základě znalostí vaší společnosti.',
    followedOne: 'Použita {count} dovednost',
    followedMany: 'Použito {count} dovedností',
    followedSubtitle: 'Přečteno před touto odpovědí',
    howFound: 'Nalezena pro „{need}“',
    howFoundPlain: 'Nalezena pro tuto žádost',
    howPicked: 'Vybrána pro tento chat',
    howAgent: 'Tento agent ji používá vždy',
    howRead: 'Přečteno pro tuto odpověď',
    openFollowed: 'Otevřít {name}, verze {revision}',
    followedEyebrow: 'Dovednost · Verze {revision}',
    followedContext: 'Použito pro tuto odpověď · Verze {revision}',
    followedRecordNote:
      'Tento záznam ukazuje, co Poneva dostala před napsáním této odpovědi, nikoli ověření, že byl dodržen každý krok.',
    openInSkills: 'Otevřít v dovednostech',
    newerVersion: 'Existuje novější verze (verze {revision}).',
    viewNewer: 'Zobrazit verzi {revision}',
    versionUnavailable: 'Verze {revision} už vám není dostupná. Toto je aktuální verze.',
    versionLoadFailed:
      'Verzi {revision} se nepodařilo načíst. Zkuste to znovu a zobrazte pokyny použité pro tuto odpověď.',
    retryVersion: 'Zkusit znovu',
    lookedFor: 'Hledáno',
    searched: 'Vyhledáno',
    searchedSkills: 'Firemní dovednosti podle řádku „Použít, když“',
    found: 'Nalezeno',
    bestFit: 'Nejlepší shoda',
    noneFound: 'Této dovednosti žádný výsledek neodpovídá.',
    skillLabel: 'Dovednost',
    versionLabel: 'Verze {revision}',
  },
  da: {
    pageLead:
      'Sådan udføres godt arbejde her. Poneva finder den Færdighed, der passer, i enhver chat eller agent, og følger den oven på jeres virksomhedsviden.',
    search: 'Find Færdigheder efter betydning…',
    pageNote:
      'Skriv og rediger Færdigheder i chatten. Poneva følger offentliggjorte Færdigheder, hvor de passer.',
    cardApplies: 'Anvendes, når den passer',
    cardDraft: 'Kladde · Ikke offentliggjort',
    cardNewDraft: 'Ny kladde',
    cardVersion: 'v{revision}',
    emptyTitle: 'Start med noget, du ofte gør.',
    emptyBody:
      'Fortæl Poneva, hvordan en bestemt type arbejde udføres her. Den skriver instruktionerne sammen med dig på baggrund af jeres virksomhedsviden, og alle chats og agenter følger dem, når du har offentliggjort dem.',
    emptyAction: 'Opret din første Færdighed',
    draftCard: 'Kladde {revision} · Åbn instruktioner',
    versionPublished: 'Version {revision} · Offentliggjort',
    versionDraft: 'Version {revision} · Kladde',
    versionEarlier: 'Version {revision} · Tidligere',
    publishedVersion: 'Version {revision} er den offentliggjorte version.',
    useInChat: 'Brug i chat',
    editInChat: 'Rediger i chat',
    editLatest: 'Rediger seneste i chat',
    applies: 'Anvendes',
    appliesWhenItFits: 'Når den passer',
    appliesHint: 'Poneva finder den via linjen Brug når i enhver chat eller agentkørsel.',
    filesRow: 'Filer',
    tabInstructions: 'Instruktioner',
    tabFollowedIn: 'Anvendt i',
    tabVersions: 'Versioner',
    followedInAnswersOne: 'Anvendt i {count} svar denne uge.',
    followedInAnswersOther: 'Anvendt i {count} svar denne uge.',
    followedInEmpty:
      'Ikke anvendt endnu. Når en chat eller agentkørsel anvender denne Færdighed, vises det her.',
    followedInFailed: 'Det var ikke muligt at indlæse, hvor denne Færdighed blev anvendt.',
    versionsCurrent: 'Aktuel',
    versionsView: 'Vis',
    restore: 'Gendan som ny kladde',
    restored: 'Tidligere instruktioner er gendannet som en ny kladde.',
    restoreFailed: 'Denne version kunne ikke gendannes.',
    publishedFor: 'Offentliggjort for {company}.',
    editStaysDraft:
      'Ændringer, du foretager i chatten, forbliver en kladde, indtil du offentliggør dem.',
    draftOnlyYou: 'Kun du kan se denne kladde.',
    draftShared: 'Kun du og de personer, den er delt med, kan se denne kladde.',
    publishHint: 'Offentliggør den, når teksten er klar. Den behøver ikke at blive afprøvet først.',
    newerDraftHint:
      'Offentliggør denne version, når teksten er klar. Den behøver ikke at blive afprøvet først.',
    publish: 'Offentliggør',
    requestPublication: 'Anmod om offentliggørelse',
    publishing: 'Offentliggør…',
    published: 'Offentliggjort for din virksomhed.',
    requested: 'Sendt til godkendelse. En udgiver gennemgår denne version i sin indbakke.',
    publishFailed: 'Denne version kunne ikke offentliggøres. Prøv igen.',
    companyFile: 'Virksomhedsfil',
    discussPassage: 'Drøft dette afsnit',
    contentLabel: 'Færdighedsindhold',
    unpublish: 'Afpublicer virksomhedsudgave',
    unpublishQuestion:
      'Afpublicer virksomhedsudgaven? Dine kolleger mister adgang til de offentliggjorte instruktioner, og chats og agenter holder op med at følge dem.',
    confirmUnpublish: 'Bekræft afpublicering',
    unpublishing: 'Afpublicerer…',
    cancel: 'Annuller',
    unpublished: 'Virksomhedsudgaven er afpubliceret. Kladder er uændrede.',
    unpublishFailed: 'Denne Færdighed kunne ikke afpubliceres.',
    newSkillTitle: 'Hvad skal denne Færdighed lære?',
    newSkillBody:
      'Beskriv, hvordan arbejdet udføres her. Poneva udarbejder instruktionerne her på siden ud fra jeres virksomhedsviden.',
    followedOne: 'Anvendte {count} Færdighed',
    followedMany: 'Anvendte {count} Færdigheder',
    followedSubtitle: 'Læst før dette svar',
    howFound: 'Fundet til “{need}”',
    howFoundPlain: 'Fundet til denne anmodning',
    howPicked: 'Valgt til denne chat',
    howAgent: 'Følges altid af denne agent',
    howRead: 'Læst til dette svar',
    openFollowed: 'Åbn {name}, version {revision}',
    followedEyebrow: 'Færdighed · Version {revision}',
    followedContext: 'Anvendt til dette svar · Version {revision}',
    followedRecordNote:
      'Dette registrerer, hvad Poneva fik, før svaret blev skrevet, ikke en kontrol af, at hvert trin blev fulgt.',
    openInSkills: 'Åbn i Færdigheder',
    newerVersion: 'Der findes en nyere version (version {revision}).',
    viewNewer: 'Vis version {revision}',
    versionUnavailable:
      'Version {revision} er ikke længere tilgængelig for dig. Dette er den aktuelle version.',
    versionLoadFailed:
      'Version {revision} kunne ikke indlæses. Prøv igen for at se de instruktioner, der blev brugt til dette svar.',
    retryVersion: 'Prøv igen',
    lookedFor: 'Søgt efter',
    searched: 'Søgte',
    searchedSkills: 'Virksomhedens færdigheder efter deres linje med Anvend når',
    found: 'Fundet',
    bestFit: 'Bedste match',
    noneFound: 'Ingen færdighed matchede denne søgning.',
    skillLabel: 'Færdighed',
    versionLabel: 'Version {revision}',
  },
  nl: {
    pageLead:
      'Zo krijg je hier goed werk gedaan. Poneva vindt de passende vaardigheid, in elke chat of agent, en volgt die boven op je bedrijfskennis.',
    search: 'Vaardigheden zoeken op betekenis…',
    pageNote:
      'Schrijf en wijzig vaardigheden in de chat. Poneva volgt gepubliceerde vaardigheden overal waar ze passen.',
    cardApplies: 'Van toepassing wanneer passend',
    cardDraft: 'Concept · Niet gepubliceerd',
    cardNewDraft: 'Nieuw concept',
    cardVersion: 'v{revision}',
    emptyTitle: 'Begin met iets wat je vaak doet.',
    emptyBody:
      'Vertel Poneva hoe een bepaald soort werk hier wordt gedaan. Samen schrijf je de instructies, gebaseerd op je bedrijfskennis, en elke chat en agent volgt ze zodra je ze publiceert.',
    emptyAction: 'Maak je eerste vaardigheid',
    draftCard: 'Concept {revision} · Instructies openen',
    versionPublished: 'Versie {revision} · Gepubliceerd',
    versionDraft: 'Versie {revision} · Concept',
    versionEarlier: 'Versie {revision} · Eerder',
    publishedVersion: 'Versie {revision} is de gepubliceerde versie.',
    useInChat: 'In chat gebruiken',
    editInChat: 'In chat bewerken',
    editLatest: 'Laatste versie in chat bewerken',
    applies: 'Van toepassing',
    appliesWhenItFits: 'Wanneer passend',
    appliesHint:
      'Poneva vindt de vaardigheid via de regel ‘Gebruiken wanneer’, in elke chat of agentuitvoering.',
    filesRow: 'Bestanden',
    tabInstructions: 'Instructies',
    tabFollowedIn: 'Gevolgd in',
    tabVersions: 'Versies',
    followedInAnswersOne: 'Gevolgd in {count} antwoord deze week.',
    followedInAnswersOther: 'Gevolgd in {count} antwoorden deze week.',
    followedInEmpty:
      'Nog niet gevolgd. Wanneer een chat of agentuitvoering deze vaardigheid volgt, wordt dat hier weergegeven.',
    followedInFailed: 'Kan niet laden waar deze vaardigheid is gevolgd.',
    versionsCurrent: 'Huidig',
    versionsView: 'Bekijken',
    restore: 'Als nieuw concept herstellen',
    restored: 'Eerdere instructies zijn als nieuw concept hersteld.',
    restoreFailed: 'Deze versie kon niet worden hersteld.',
    publishedFor: 'Gepubliceerd voor {company}.',
    editStaysDraft:
      'Wijzigingen die je in de chat maakt, blijven een concept totdat je ze publiceert.',
    draftOnlyYou: 'Alleen jij kunt dit concept zien.',
    draftShared: 'Alleen jij en de mensen met wie het is gedeeld, kunnen dit concept zien.',
    publishHint: 'Publiceer zodra de tekst goed klinkt. Je hoeft niets eerst uit te proberen.',
    newerDraftHint:
      'Publiceer deze versie zodra de tekst goed klinkt. Je hoeft niets eerst uit te proberen.',
    publish: 'Publiceren',
    requestPublication: 'Publicatie aanvragen',
    publishing: 'Bezig met publiceren…',
    published: 'Gepubliceerd voor je bedrijf.',
    requested: 'Ter goedkeuring verzonden. Een uitgever beoordeelt deze versie in diens Inbox.',
    publishFailed: 'Deze versie kon niet worden gepubliceerd. Probeer het opnieuw.',
    companyFile: 'Bedrijfsbestand',
    discussPassage: 'Deze passage bespreken',
    contentLabel: 'Inhoud van de vaardigheid',
    unpublish: 'Bedrijfsversie intrekken',
    unpublishQuestion:
      'De bedrijfsversie intrekken? Collega’s verliezen toegang tot de gepubliceerde instructies en chats en agents volgen deze niet meer.',
    confirmUnpublish: 'Intrekking bevestigen',
    unpublishing: 'Bezig met intrekken…',
    cancel: 'Annuleren',
    unpublished: 'Bedrijfsversie ingetrokken. Concepten zijn ongewijzigd.',
    unpublishFailed: 'Deze vaardigheid kon niet worden ingetrokken.',
    newSkillTitle: 'Wat moet deze vaardigheid leren?',
    newSkillBody:
      'Beschrijf hoe het werk hier wordt gedaan. Poneva maakt hier een concept van de instructies, gebaseerd op je bedrijfskennis.',
    followedOne: '{count} vaardigheid gevolgd',
    followedMany: '{count} vaardigheden gevolgd',
    followedSubtitle: 'Gelezen vóór dit antwoord',
    howFound: 'Gevonden voor “{need}”',
    howFoundPlain: 'Gevonden voor dit verzoek',
    howPicked: 'Geselecteerd voor deze chat',
    howAgent: 'Altijd gevolgd door deze agent',
    howRead: 'Gelezen voor dit antwoord',
    openFollowed: '{name}, versie {revision} openen',
    followedEyebrow: 'Vaardigheid · Versie {revision}',
    followedContext: 'Gevolgd voor dit antwoord · Versie {revision}',
    followedRecordNote:
      'Dit registreert wat Poneva kreeg vóór het schrijven van dit antwoord, niet of elke stap daadwerkelijk is gevolgd.',
    openInSkills: 'Openen in Vaardigheden',
    newerVersion: 'Er is een nieuwere versie (versie {revision}).',
    viewNewer: 'Versie {revision} bekijken',
    versionUnavailable:
      'Versie {revision} is niet langer voor jou beschikbaar. Dit is de huidige versie.',
    versionLoadFailed:
      'Versie {revision} kon niet worden geladen. Probeer het opnieuw om de instructies te bekijken die voor dit antwoord zijn gebruikt.',
    retryVersion: 'Opnieuw proberen',
    lookedFor: 'Gezocht naar',
    searched: 'Doorzocht',
    searchedSkills: "Bedrijfsvaardigheden, op basis van de regel 'Wanneer gebruiken'",
    found: 'Gevonden',
    bestFit: 'Beste match',
    noneFound: 'Geen vaardigheid gevonden die overeenkomt met deze zoekopdracht.',
    skillLabel: 'Vaardigheid',
    versionLabel: 'Versie {revision}',
  },
  en: {
    pageLead:
      'How good work gets done here. Poneva finds the Skill that fits, in any chat or agent, and follows it on top of your company knowledge.',
    search: 'Find Skills by meaning…',
    pageNote: 'Write and change Skills in chat. Poneva follows published Skills wherever they fit.',
    cardApplies: 'Applies when it fits',
    cardDraft: 'Draft · Not published',
    cardNewDraft: 'New draft',
    cardVersion: 'v{revision}',
    emptyTitle: 'Start with something you do often.',
    emptyBody:
      'Tell Poneva how a kind of work is done here. It writes the instructions with you, on top of your company knowledge, and every chat and agent follows them once you publish.',
    emptyAction: 'Create your first Skill',
    draftCard: 'Draft {revision} · Open instructions',
    versionPublished: 'Version {revision} · Published',
    versionDraft: 'Version {revision} · Draft',
    versionEarlier: 'Version {revision} · Earlier',
    publishedVersion: 'Version {revision} is the published version.',
    useInChat: 'Use in chat',
    editInChat: 'Edit in chat',
    editLatest: 'Edit latest in chat',
    applies: 'Applies',
    appliesWhenItFits: 'When it fits',
    appliesHint: 'Poneva finds it by its Use when line, in any chat or agent run.',
    filesRow: 'Files',
    tabInstructions: 'Instructions',
    tabFollowedIn: 'Followed in',
    tabVersions: 'Versions',
    followedInAnswersOne: 'Followed in {count} answer this week.',
    followedInAnswersOther: 'Followed in {count} answers this week.',
    followedInEmpty:
      'Not followed yet. When a chat or an agent run follows this Skill, it shows here.',
    followedInFailed: 'Where this Skill was followed could not be loaded.',
    versionsCurrent: 'Current',
    versionsView: 'View',
    restore: 'Restore as new draft',
    restored: 'Earlier instructions restored as a new draft.',
    restoreFailed: 'This version could not be restored.',
    publishedFor: 'Published for {company}.',
    editStaysDraft: 'Changes you make in chat stay a draft until you publish them.',
    draftOnlyYou: 'Only you can see this draft.',
    draftShared: 'Only you and the people it is shared with can see this draft.',
    publishHint: 'Publish when it reads right. Nothing has to be tried first.',
    newerDraftHint: 'Publish this version when it reads right. Nothing has to be tried first.',
    publish: 'Publish',
    requestPublication: 'Request publication',
    publishing: 'Publishing…',
    published: 'Published for your company.',
    requested: 'Sent for approval. A publisher reviews this version in their Inbox.',
    publishFailed: 'This version could not be published. Try again.',
    companyFile: 'Company file',
    discussPassage: 'Discuss this passage',
    contentLabel: 'Skill content',
    unpublish: 'Unpublish company version',
    unpublishQuestion:
      'Unpublish the company version? Colleagues will lose access to its published instructions, and chats and agents stop following it.',
    confirmUnpublish: 'Confirm unpublish',
    unpublishing: 'Unpublishing…',
    cancel: 'Cancel',
    unpublished: 'Company version unpublished. Drafts are unchanged.',
    unpublishFailed: 'Could not unpublish this Skill.',
    newSkillTitle: 'What should this Skill teach?',
    newSkillBody:
      'Describe how the work is done here. Poneva drafts the instructions on this side, from your company knowledge.',
    followedOne: 'Followed {count} Skill',
    followedMany: 'Followed {count} Skills',
    followedSubtitle: 'Read before this answer',
    howFound: 'Found for “{need}”',
    howFoundPlain: 'Found for this request',
    howPicked: 'Picked for this chat',
    howAgent: 'Always followed by this agent',
    howRead: 'Read for this answer',
    openFollowed: 'Open {name}, version {revision}',
    followedEyebrow: 'Skill · Version {revision}',
    followedContext: 'Followed for this answer · Version {revision}',
    followedRecordNote:
      'This records what Poneva was given before writing this answer, not a check that every step was followed.',
    openInSkills: 'Open in Skills',
    newerVersion: 'A newer version exists (Version {revision}).',
    viewNewer: 'View version {revision}',
    versionUnavailable:
      'Version {revision} is no longer available to you. This is the current version.',
    versionLoadFailed:
      'Could not load version {revision}. Try again to see the instructions used for this answer.',
    retryVersion: 'Try again',
    lookedFor: 'Looked for',
    searched: 'Searched',
    searchedSkills: 'Company Skills, by their Use when line',
    found: 'Found',
    bestFit: 'Best fit',
    noneFound: 'No Skill matched this search.',
    skillLabel: 'Skill',
    versionLabel: 'Version {revision}',
  },
  et: {
    pageLead:
      'Nii tehakse siin head tööd. Poneva leiab sobiva oskuse igas vestluses või agendis ja järgib seda teie ettevõtte teadmiste põhjal.',
    search: 'Leia oskusi tähenduse järgi…',
    pageNote:
      'Kirjuta ja muuda oskusi vestluses. Poneva järgib avaldatud oskusi kõikjal, kus need sobivad.',
    cardApplies: 'Rakendub, kui sobib',
    cardDraft: 'Mustand · Avaldamata',
    cardNewDraft: 'Uus mustand',
    cardVersion: 'v{revision}',
    emptyTitle: 'Alusta millestki, mida teed sageli.',
    emptyBody:
      'Kirjelda Ponevale, kuidas siin mingit tööd tehakse. See koostab juhised koos sinuga teie ettevõtte teadmiste põhjal ning kõik vestlused ja agendid järgivad neid pärast avaldamist.',
    emptyAction: 'Loo oma esimene oskus',
    draftCard: 'Mustand {revision} · Ava juhised',
    versionPublished: 'Versioon {revision} · Avaldatud',
    versionDraft: 'Versioon {revision} · Mustand',
    versionEarlier: 'Versioon {revision} · Varasem',
    publishedVersion: 'Versioon {revision} on avaldatud versioon.',
    useInChat: 'Kasuta vestluses',
    editInChat: 'Muuda vestluses',
    editLatest: 'Muuda uusimat versiooni vestluses',
    applies: 'Rakendub',
    appliesWhenItFits: 'Kui sobib',
    appliesHint: 'Poneva leiab selle rea „Kasuta, kui” järgi igas vestluses või agendikäituses.',
    filesRow: 'Failid',
    tabInstructions: 'Juhised',
    tabFollowedIn: 'Järgitud kohas',
    tabVersions: 'Versioonid',
    followedInAnswersOne: 'Järgitud sel nädalal {count} vastuses.',
    followedInAnswersOther: 'Järgitud sel nädalal {count} vastuses.',
    followedInEmpty:
      'Veel pole järgitud. Kui vestlus või agendikäitus seda oskust järgib, kuvatakse see siin.',
    followedInFailed: 'Kohad, kus seda oskust järgiti, ei olnud laaditavad.',
    versionsCurrent: 'Praegune',
    versionsView: 'Vaata',
    restore: 'Taasta uue mustandina',
    restored: 'Varasemad juhised taastati uue mustandina.',
    restoreFailed: 'Seda versiooni ei saanud taastada.',
    publishedFor: 'Avaldatud ettevõttele {company}.',
    editStaysDraft: 'Vestluses tehtud muudatused jäävad mustandiks, kuni need avaldad.',
    draftOnlyYou: 'Ainult sina näed seda mustandit.',
    draftShared: 'Ainult sina ja inimesed, kellega seda jagatakse, näete seda mustandit.',
    publishHint: 'Avalda, kui tekst on õige. Seda pole vaja enne proovida.',
    newerDraftHint: 'Avalda see versioon, kui tekst on õige. Seda pole vaja enne proovida.',
    publish: 'Avalda',
    requestPublication: 'Taotle avaldamist',
    publishing: 'Avaldamine…',
    published: 'Avaldatud teie ettevõttele.',
    requested: 'Saadetud kinnitamiseks. Avaldaja vaatab selle versiooni oma sisendkastis üle.',
    publishFailed: 'Seda versiooni ei saanud avaldada. Proovi uuesti.',
    companyFile: 'Ettevõtte fail',
    discussPassage: 'Arutle selle lõigu üle',
    contentLabel: 'Oskuse sisu',
    unpublish: 'Tühista ettevõtte versiooni avaldamine',
    unpublishQuestion:
      'Kas tühistada ettevõtte versiooni avaldamine? Kolleegid kaotavad juurdepääsu selle avaldatud juhistele ning vestlused ja agendid lõpetavad selle järgimise.',
    confirmUnpublish: 'Kinnita avaldamise tühistamine',
    unpublishing: 'Avaldamise tühistamine…',
    cancel: 'Loobu',
    unpublished: 'Ettevõtte versiooni avaldamine tühistati. Mustandid jäid muutmata.',
    unpublishFailed: 'Selle oskuse avaldamist ei saanud tühistada.',
    newSkillTitle: 'Mida peaks see oskus õpetama?',
    newSkillBody:
      'Kirjelda, kuidas siin tööd tehakse. Poneva koostab juhised siin ettevõtte teadmiste põhjal.',
    followedOne: 'Järgitud {count} oskus',
    followedMany: 'Järgitud {count} oskust',
    followedSubtitle: 'Loe enne seda vastust',
    howFound: 'Leitud vajaduse „{need}” jaoks',
    howFoundPlain: 'Leitud selle päringu jaoks',
    howPicked: 'Valitud sellesse vestlusse',
    howAgent: 'See agent järgib alati',
    howRead: 'Loetud selle vastuse jaoks',
    openFollowed: 'Ava {name}, versioon {revision}',
    followedEyebrow: 'Oskus · Versioon {revision}',
    followedContext: 'Järgitud selle vastuse jaoks · Versioon {revision}',
    followedRecordNote:
      'See salvestab, mida Ponevale enne selle vastuse kirjutamist anti, mitte seda, kas iga sammu järgiti.',
    openInSkills: 'Ava jaotises Oskused',
    newerVersion: 'Saadaval on uuem versioon (versioon {revision}).',
    viewNewer: 'Vaata versiooni {revision}',
    versionUnavailable: 'Versioon {revision} pole sulle enam saadaval. See on praegune versioon.',
    versionLoadFailed:
      'Versiooni {revision} ei saanud laadida. Proovi uuesti, et näha selle vastuse jaoks kasutatud juhiseid.',
    retryVersion: 'Proovi uuesti',
    lookedFor: 'Otsiti',
    searched: 'Otsing tehtud',
    searchedSkills: 'Ettevõtte oskused nende kasutusviisi järgi',
    found: 'Leiti',
    bestFit: 'Parim vaste',
    noneFound: 'Sellele otsingule vastavat oskust ei leitud.',
    skillLabel: 'Oskus',
    versionLabel: 'Versioon {revision}',
  },
  fi: {
    pageLead:
      'Näin täällä saadaan hyvää työtä aikaan. Poneva löytää sopivan taidon mistä tahansa keskustelusta tai agentista ja noudattaa sitä yrityksesi tietämyksen pohjalta.',
    search: 'Etsi taitoja merkityksen perusteella…',
    pageNote:
      'Kirjoita ja muuta taitoja keskustelussa. Poneva noudattaa julkaistuja taitoja kaikkialla, missä niitä tarvitaan.',
    cardApplies: 'Käytössä, kun sopii tilanteeseen',
    cardDraft: 'Luonnos · Ei julkaistu',
    cardNewDraft: 'Uusi luonnos',
    cardVersion: 'v{revision}',
    emptyTitle: 'Aloita jostain, mitä teet usein.',
    emptyBody:
      'Kerro Ponevalle, miten tietynlainen työ täällä tehdään. Se kirjoittaa ohjeet kanssasi yrityksesi tietämyksen pohjalta, ja kaikki keskustelut ja agentit noudattavat niitä, kun julkaiset ne.',
    emptyAction: 'Luo ensimmäinen taitosi',
    draftCard: 'Luonnos {revision} · Avaa ohjeet',
    versionPublished: 'Versio {revision} · Julkaistu',
    versionDraft: 'Versio {revision} · Luonnos',
    versionEarlier: 'Versio {revision} · Aiempi',
    publishedVersion: 'Versio {revision} on julkaistu versio.',
    useInChat: 'Käytä keskustelussa',
    editInChat: 'Muokkaa keskustelussa',
    editLatest: 'Muokkaa uusinta keskustelussa',
    applies: 'Käytössä',
    appliesWhenItFits: 'Kun sopii tilanteeseen',
    appliesHint:
      'Poneva löytää taidon sen Käytä, kun -rivin perusteella mistä tahansa keskustelusta tai agentin suorituksesta.',
    filesRow: 'Tiedostot',
    tabInstructions: 'Ohjeet',
    tabFollowedIn: 'Noudatettu kohteissa',
    tabVersions: 'Versiot',
    followedInAnswersOne: 'Noudatettu {count} vastauksessa tällä viikolla.',
    followedInAnswersOther: 'Noudatettu {count} vastauksessa tällä viikolla.',
    followedInEmpty:
      'Ei vielä noudatettu. Kun keskustelu tai agentin suoritus noudattaa tätä taitoa, se näkyy tässä.',
    followedInFailed: 'Tietoja siitä, missä tätä taitoa noudatettiin, ei voitu ladata.',
    versionsCurrent: 'Nykyinen',
    versionsView: 'Näytä',
    restore: 'Palauta uutena luonnoksena',
    restored: 'Aiemmat ohjeet palautettiin uutena luonnoksena.',
    restoreFailed: 'Tätä versiota ei voitu palauttaa.',
    publishedFor: 'Julkaistu yritykselle {company}.',
    editStaysDraft: 'Keskustelussa tekemäsi muutokset säilyvät luonnoksena, kunnes julkaiset ne.',
    draftOnlyYou: 'Vain sinä voit nähdä tämän luonnoksen.',
    draftShared: 'Vain sinä ja ne, joiden kanssa se on jaettu, voitte nähdä tämän luonnoksen.',
    publishHint: 'Julkaise, kun teksti on mielestäsi valmis. Mitään ei tarvitse kokeilla ensin.',
    newerDraftHint:
      'Julkaise tämä versio, kun teksti on mielestäsi valmis. Mitään ei tarvitse kokeilla ensin.',
    publish: 'Julkaise',
    requestPublication: 'Pyydä julkaisemista',
    publishing: 'Julkaistaan…',
    published: 'Julkaistu yrityksellesi.',
    requested:
      'Lähetetty hyväksyttäväksi. Julkaisija tarkistaa tämän version Saapuneet-kansiossaan.',
    publishFailed: 'Tätä versiota ei voitu julkaista. Yritä uudelleen.',
    companyFile: 'Yrityksen tiedosto',
    discussPassage: 'Keskustele tästä kohdasta',
    contentLabel: 'Taido sisältö',
    unpublish: 'Poista yritysversio julkaisusta',
    unpublishQuestion:
      'Poistetaanko yritysversio julkaisusta? Kollegat menettävät pääsyn sen julkaistuihin ohjeisiin, eivätkä keskustelut ja agentit enää noudata niitä.',
    confirmUnpublish: 'Vahvista julkaisun poisto',
    unpublishing: 'Poistetaan julkaisusta…',
    cancel: 'Peruuta',
    unpublished: 'Yritysversio poistettiin julkaisusta. Luonnokset säilyivät ennallaan.',
    unpublishFailed: 'Tätä taitoa ei voitu poistaa julkaisusta.',
    newSkillTitle: 'Mitä tämän taidon pitäisi opettaa?',
    newSkillBody:
      'Kuvaile, miten työ täällä tehdään. Poneva laatii ohjeet tässä näkymässä yrityksesi tietämyksen pohjalta.',
    followedOne: 'Noudatettu {count} taitoa',
    followedMany: 'Noudatettu {count} taitoa',
    followedSubtitle: 'Luettu ennen tätä vastausta',
    howFound: 'Löytyi tarpeeseen ”{need}”',
    howFoundPlain: 'Löytyi tätä pyyntöä varten',
    howPicked: 'Valittu tähän keskusteluun',
    howAgent: 'Tämä agentti noudattaa aina',
    howRead: 'Luettu tätä vastausta varten',
    openFollowed: 'Avaa {name}, versio {revision}',
    followedEyebrow: 'Taito · Versio {revision}',
    followedContext: 'Noudatettu tätä vastausta varten · Versio {revision}',
    followedRecordNote:
      'Tämä kertoo, mitä Ponevalle annettiin ennen vastauksen kirjoittamista, ei sitä, noudatettiinko jokaista vaihetta.',
    openInSkills: 'Avaa taidoissa',
    newerVersion: 'Uudempi versio on saatavilla (versio {revision}).',
    viewNewer: 'Näytä versio {revision}',
    versionUnavailable: 'Versio {revision} ei ole enää käytettävissäsi. Tämä on nykyinen versio.',
    versionLoadFailed:
      'Versiota {revision} ei voitu ladata. Yritä uudelleen nähdäksesi tätä vastausta varten käytetyt ohjeet.',
    retryVersion: 'Yritä uudelleen',
    lookedFor: 'Haettiin',
    searched: 'Haettiin',
    searchedSkills: 'Yrityksen taidot niiden käyttötarkoitusrivin mukaan',
    found: 'Löytyi',
    bestFit: 'Paras vastaavuus',
    noneFound: 'Yksikään taito ei vastannut hakua.',
    skillLabel: 'Taito',
    versionLabel: 'Versio {revision}',
  },
  fr: {
    pageLead:
      'Comment le bon travail se fait ici. Poneva trouve la compétence adaptée, dans n’importe quel chat ou agent, et la suit en s’appuyant sur les connaissances de votre entreprise.',
    search: 'Rechercher des compétences par signification…',
    pageNote:
      'Rédigez et modifiez des compétences dans le chat. Poneva suit les compétences publiées partout où elles s’appliquent.',
    cardApplies: 'S’applique lorsque c’est pertinent',
    cardDraft: 'Brouillon · Non publiée',
    cardNewDraft: 'Nouveau brouillon',
    cardVersion: 'v{revision}',
    emptyTitle: 'Commencez par quelque chose que vous faites souvent.',
    emptyBody:
      'Expliquez à Poneva comment un type de travail est réalisé ici. Elle rédige les instructions avec vous, en s’appuyant sur les connaissances de votre entreprise, puis chaque chat et chaque agent les suit dès que vous les publiez.',
    emptyAction: 'Créer votre première compétence',
    draftCard: 'Brouillon {revision} · Ouvrir les instructions',
    versionPublished: 'Version {revision} · Publiée',
    versionDraft: 'Version {revision} · Brouillon',
    versionEarlier: 'Version {revision} · Antérieure',
    publishedVersion: 'La version {revision} est la version publiée.',
    useInChat: 'Utiliser dans le chat',
    editInChat: 'Modifier dans le chat',
    editLatest: 'Modifier la dernière version dans le chat',
    applies: 'S’applique',
    appliesWhenItFits: 'Lorsque c’est pertinent',
    appliesHint:
      'Poneva la trouve grâce à sa ligne « À utiliser quand », dans n’importe quel chat ou exécution d’agent.',
    filesRow: 'Fichiers',
    tabInstructions: 'Instructions',
    tabFollowedIn: 'Suivie dans',
    tabVersions: 'Versions',
    followedInAnswersOne: 'Suivie dans {count} réponse cette semaine.',
    followedInAnswersOther: 'Suivie dans {count} réponses cette semaine.',
    followedInEmpty:
      'Pas encore suivie. Lorsqu’un chat ou une exécution d’agent suit cette compétence, elle apparaît ici.',
    followedInFailed: 'Impossible de charger les endroits où cette compétence a été suivie.',
    versionsCurrent: 'Actuelle',
    versionsView: 'Afficher',
    restore: 'Restaurer comme nouveau brouillon',
    restored: 'Les instructions antérieures ont été restaurées comme nouveau brouillon.',
    restoreFailed: 'Cette version n’a pas pu être restaurée.',
    publishedFor: 'Publiée pour {company}.',
    editStaysDraft:
      'Les modifications apportées dans le chat restent à l’état de brouillon jusqu’à leur publication.',
    draftOnlyYou: 'Vous seul pouvez voir ce brouillon.',
    draftShared: 'Vous et les personnes avec qui ce brouillon est partagé pouvez le voir.',
    publishHint:
      'Publiez-le lorsque le texte vous convient. Rien ne doit être essayé au préalable.',
    newerDraftHint:
      'Publiez cette version lorsque le texte vous convient. Rien ne doit être essayé au préalable.',
    publish: 'Publier',
    requestPublication: 'Demander la publication',
    publishing: 'Publication…',
    published: 'Publiée pour votre entreprise.',
    requested:
      'Envoyée pour approbation. Un éditeur examine cette version dans sa boîte de réception.',
    publishFailed: 'Cette version n’a pas pu être publiée. Réessayez.',
    companyFile: 'Fichier de l’entreprise',
    discussPassage: 'Discuter de ce passage',
    contentLabel: 'Contenu de la compétence',
    unpublish: 'Dépublier la version de l’entreprise',
    unpublishQuestion:
      'Dépublier la version de l’entreprise ? Les collègues n’auront plus accès à ses instructions publiées, et les chats et agents cesseront de la suivre.',
    confirmUnpublish: 'Confirmer la dépublication',
    unpublishing: 'Dépublication…',
    cancel: 'Annuler',
    unpublished: 'Version de l’entreprise dépubliée. Les brouillons restent inchangés.',
    unpublishFailed: 'Impossible de dépublier cette compétence.',
    newSkillTitle: 'Que doit enseigner cette compétence ?',
    newSkillBody:
      'Décrivez comment le travail est réalisé ici. Poneva rédige les instructions de votre côté, à partir des connaissances de votre entreprise.',
    followedOne: '{count} compétence suivie',
    followedMany: '{count} compétences suivies',
    followedSubtitle: 'Lue avant cette réponse',
    howFound: 'Trouvée pour « {need} »',
    howFoundPlain: 'Trouvée pour cette demande',
    howPicked: 'Sélectionnée pour ce chat',
    howAgent: 'Toujours suivie par cet agent',
    howRead: 'Lue pour cette réponse',
    openFollowed: 'Ouvrir {name}, version {revision}',
    followedEyebrow: 'Compétence · Version {revision}',
    followedContext: 'Suivie pour cette réponse · Version {revision}',
    followedRecordNote:
      'Cela indique ce qui a été fourni à Poneva avant la rédaction de cette réponse, et non que chaque étape a été suivie.',
    openInSkills: 'Ouvrir dans Compétences',
    newerVersion: 'Une version plus récente existe (version {revision}).',
    viewNewer: 'Afficher la version {revision}',
    versionUnavailable:
      'La version {revision} ne vous est plus accessible. Il s’agit de la version actuelle.',
    versionLoadFailed:
      'Impossible de charger la version {revision}. Réessayez pour voir les instructions utilisées pour cette réponse.',
    retryVersion: 'Réessayer',
    lookedFor: 'Recherché',
    searched: 'Recherche effectuée',
    searchedSkills: 'Compétences de l’entreprise, selon leur ligne « À utiliser quand »',
    found: 'Résultat trouvé',
    bestFit: 'Correspondance la plus pertinente',
    noneFound: 'Aucune compétence ne correspond à cette recherche.',
    skillLabel: 'Compétence',
    versionLabel: 'Version {revision}',
  },
  de: {
    pageLead:
      'So wird hier gute Arbeit erledigt. Poneva findet in jedem Chat oder Agenten die passende Fähigkeit und wendet sie zusätzlich zu Ihrem Unternehmenswissen an.',
    search: 'Fähigkeiten nach Bedeutung suchen…',
    pageNote:
      'Schreiben und ändern Sie Fähigkeiten im Chat. Poneva wendet veröffentlichte Fähigkeiten überall dort an, wo sie passen.',
    cardApplies: 'Wird angewendet, wenn es passt',
    cardDraft: 'Entwurf · Nicht veröffentlicht',
    cardNewDraft: 'Neuer Entwurf',
    cardVersion: 'v{revision}',
    emptyTitle: 'Beginnen Sie mit etwas, das Sie oft tun.',
    emptyBody:
      'Erklären Sie Poneva, wie diese Art von Arbeit hier erledigt wird. Poneva erstellt gemeinsam mit Ihnen die Anweisungen auf Grundlage Ihres Unternehmenswissens. Nach der Veröffentlichung werden sie von jedem Chat und Agenten befolgt.',
    emptyAction: 'Erste Fähigkeit erstellen',
    draftCard: 'Entwurf {revision} · Anweisungen öffnen',
    versionPublished: 'Version {revision} · Veröffentlicht',
    versionDraft: 'Version {revision} · Entwurf',
    versionEarlier: 'Version {revision} · Früher',
    publishedVersion: 'Version {revision} ist die veröffentlichte Version.',
    useInChat: 'Im Chat verwenden',
    editInChat: 'Im Chat bearbeiten',
    editLatest: 'Neueste Version im Chat bearbeiten',
    applies: 'Wird angewendet',
    appliesWhenItFits: 'Wenn es passt',
    appliesHint:
      'Poneva findet sie anhand der Zeile „Verwenden, wenn …“ in jedem Chat oder Agentenlauf.',
    filesRow: 'Dateien',
    tabInstructions: 'Anweisungen',
    tabFollowedIn: 'Angewendet in',
    tabVersions: 'Versionen',
    followedInAnswersOne: 'In {count} Antwort diese Woche angewendet.',
    followedInAnswersOther: 'In {count} Antworten diese Woche angewendet.',
    followedInEmpty:
      'Noch nicht angewendet. Wenn ein Chat oder Agentenlauf diese Fähigkeit verwendet, wird sie hier angezeigt.',
    followedInFailed:
      'Die Orte, an denen diese Fähigkeit angewendet wurde, konnten nicht geladen werden.',
    versionsCurrent: 'Aktuell',
    versionsView: 'Ansehen',
    restore: 'Als neuen Entwurf wiederherstellen',
    restored: 'Frühere Anweisungen als neuen Entwurf wiederhergestellt.',
    restoreFailed: 'Diese Version konnte nicht wiederhergestellt werden.',
    publishedFor: 'Für {company} veröffentlicht.',
    editStaysDraft:
      'Änderungen, die Sie im Chat vornehmen, bleiben ein Entwurf, bis Sie sie veröffentlichen.',
    draftOnlyYou: 'Nur Sie können diesen Entwurf sehen.',
    draftShared:
      'Nur Sie und die Personen, mit denen er geteilt wurde, können diesen Entwurf sehen.',
    publishHint:
      'Veröffentlichen Sie sie, sobald sie richtig formuliert sind. Zuvor muss nichts ausprobiert werden.',
    newerDraftHint:
      'Veröffentlichen Sie diese Version, sobald sie richtig formuliert ist. Zuvor muss nichts ausprobiert werden.',
    publish: 'Veröffentlichen',
    requestPublication: 'Veröffentlichung anfordern',
    publishing: 'Wird veröffentlicht…',
    published: 'Für Ihr Unternehmen veröffentlicht.',
    requested:
      'Zur Genehmigung gesendet. Eine veröffentlichungsberechtigte Person prüft diese Version in ihrem Posteingang.',
    publishFailed: 'Diese Version konnte nicht veröffentlicht werden. Versuchen Sie es erneut.',
    companyFile: 'Unternehmensdatei',
    discussPassage: 'Diesen Abschnitt besprechen',
    contentLabel: 'Inhalt der Fähigkeit',
    unpublish: 'Unternehmensversion zurückziehen',
    unpublishQuestion:
      'Unternehmensversion zurückziehen? Kolleginnen und Kollegen verlieren den Zugriff auf die veröffentlichten Anweisungen, und Chats sowie Agenten wenden sie nicht mehr an.',
    confirmUnpublish: 'Zurückziehen bestätigen',
    unpublishing: 'Wird zurückgezogen…',
    cancel: 'Abbrechen',
    unpublished: 'Unternehmensversion zurückgezogen. Entwürfe bleiben unverändert.',
    unpublishFailed: 'Diese Fähigkeit konnte nicht zurückgezogen werden.',
    newSkillTitle: 'Was soll diese Fähigkeit vermitteln?',
    newSkillBody:
      'Beschreiben Sie, wie die Arbeit hier erledigt wird. Poneva erstellt auf dieser Seite einen Entwurf der Anweisungen aus Ihrem Unternehmenswissen.',
    followedOne: '{count} Fähigkeit angewendet',
    followedMany: '{count} Fähigkeiten angewendet',
    followedSubtitle: 'Vor dieser Antwort gelesen',
    howFound: 'Für „{need}“ gefunden',
    howFoundPlain: 'Für diese Anfrage gefunden',
    howPicked: 'Für diesen Chat ausgewählt',
    howAgent: 'Von diesem Agenten immer angewendet',
    howRead: 'Für diese Antwort gelesen',
    openFollowed: '{name}, Version {revision}, öffnen',
    followedEyebrow: 'Fähigkeit · Version {revision}',
    followedContext: 'Für diese Antwort angewendet · Version {revision}',
    followedRecordNote:
      'Dies dokumentiert, was Poneva vor dem Schreiben dieser Antwort erhalten hat, nicht, ob jeder Schritt befolgt wurde.',
    openInSkills: 'In den Fähigkeiten öffnen',
    newerVersion: 'Eine neuere Version ist verfügbar (Version {revision}).',
    viewNewer: 'Version {revision} ansehen',
    versionUnavailable:
      'Version {revision} ist für Sie nicht mehr verfügbar. Dies ist die aktuelle Version.',
    versionLoadFailed:
      'Version {revision} konnte nicht geladen werden. Versuchen Sie es erneut, um die für diese Antwort verwendeten Anweisungen zu sehen.',
    retryVersion: 'Erneut versuchen',
    lookedFor: 'Gesucht nach',
    searched: 'Gesucht',
    searchedSkills: 'Unternehmensfähigkeiten anhand ihrer Zeile „Verwenden, wenn …“',
    found: 'Gefunden',
    bestFit: 'Beste Übereinstimmung',
    noneFound: 'Keine Fähigkeit entspricht dieser Suche.',
    skillLabel: 'Fähigkeit',
    versionLabel: 'Version {revision}',
  },
  el: {
    pageLead:
      'Πώς γίνεται σωστά η δουλειά εδώ. Η Poneva βρίσκει τη Δεξιότητα που ταιριάζει, σε οποιαδήποτε συνομιλία ή agent, και την εφαρμόζει πάνω από τη γνώση της εταιρείας σας.',
    search: 'Βρείτε Δεξιότητες με βάση το νόημα…',
    pageNote:
      'Γράψτε και αλλάξτε Δεξιότητες σε συνομιλία. Η Poneva εφαρμόζει τις δημοσιευμένες Δεξιότητες όπου ταιριάζουν.',
    cardApplies: 'Εφαρμόζεται όταν ταιριάζει',
    cardDraft: 'Πρόχειρο · Δεν έχει δημοσιευτεί',
    cardNewDraft: 'Νέο πρόχειρο',
    cardVersion: 'v{revision}',
    emptyTitle: 'Ξεκινήστε με κάτι που κάνετε συχνά.',
    emptyBody:
      'Πείτε στην Poneva πώς γίνεται ένα είδος εργασίας εδώ. Γράφει μαζί σας τις οδηγίες, πάνω από τη γνώση της εταιρείας σας, και κάθε συνομιλία και agent τις εφαρμόζει μόλις τις δημοσιεύσετε.',
    emptyAction: 'Δημιουργήστε την πρώτη σας Δεξιότητα',
    draftCard: 'Πρόχειρο {revision} · Άνοιγμα οδηγιών',
    versionPublished: 'Έκδοση {revision} · Δημοσιευμένη',
    versionDraft: 'Έκδοση {revision} · Πρόχειρο',
    versionEarlier: 'Έκδοση {revision} · Προηγούμενη',
    publishedVersion: 'Η έκδοση {revision} είναι η δημοσιευμένη έκδοση.',
    useInChat: 'Χρήση σε συνομιλία',
    editInChat: 'Επεξεργασία σε συνομιλία',
    editLatest: 'Επεξεργασία τελευταίας σε συνομιλία',
    applies: 'Εφαρμόζεται',
    appliesWhenItFits: 'Όταν ταιριάζει',
    appliesHint:
      'Η Poneva τη βρίσκει από τη γραμμή «Χρήση όταν», σε οποιαδήποτε συνομιλία ή εκτέλεση agent.',
    filesRow: 'Αρχεία',
    tabInstructions: 'Οδηγίες',
    tabFollowedIn: 'Εφαρμόστηκε σε',
    tabVersions: 'Εκδόσεις',
    followedInAnswersOne: 'Εφαρμόστηκε σε {count} απάντηση αυτή την εβδομάδα.',
    followedInAnswersOther: 'Εφαρμόστηκε σε {count} απαντήσεις αυτή την εβδομάδα.',
    followedInEmpty:
      'Δεν έχει εφαρμοστεί ακόμη. Όταν μια συνομιλία ή εκτέλεση agent εφαρμόσει αυτή τη Δεξιότητα, θα εμφανιστεί εδώ.',
    followedInFailed: 'Δεν ήταν δυνατή η φόρτωση των σημείων όπου εφαρμόστηκε αυτή η Δεξιότητα.',
    versionsCurrent: 'Τρέχουσα',
    versionsView: 'Προβολή',
    restore: 'Επαναφορά ως νέο πρόχειρο',
    restored: 'Οι προηγούμενες οδηγίες επαναφέρθηκαν ως νέο πρόχειρο.',
    restoreFailed: 'Δεν ήταν δυνατή η επαναφορά αυτής της έκδοσης.',
    publishedFor: 'Δημοσιεύτηκε για την {company}.',
    editStaysDraft:
      'Οι αλλαγές που κάνετε σε συνομιλία παραμένουν πρόχειρο μέχρι να τις δημοσιεύσετε.',
    draftOnlyYou: 'Μόνο εσείς μπορείτε να δείτε αυτό το πρόχειρο.',
    draftShared:
      'Μόνο εσείς και τα άτομα με τα οποία το έχετε μοιραστεί μπορείτε να δείτε αυτό το πρόχειρο.',
    publishHint: 'Δημοσιεύστε το όταν η διατύπωση είναι σωστή. Δεν χρειάζεται να δοκιμαστεί πρώτα.',
    newerDraftHint:
      'Δημοσιεύστε αυτή την έκδοση όταν η διατύπωση είναι σωστή. Δεν χρειάζεται να δοκιμαστεί πρώτα.',
    publish: 'Δημοσίευση',
    requestPublication: 'Αίτημα δημοσίευσης',
    publishing: 'Δημοσίευση…',
    published: 'Δημοσιεύτηκε για την εταιρεία σας.',
    requested: 'Στάλθηκε για έγκριση. Ένας εκδότης θα ελέγξει αυτή την έκδοση στα Εισερχόμενά του.',
    publishFailed: 'Δεν ήταν δυνατή η δημοσίευση αυτής της έκδοσης. Δοκιμάστε ξανά.',
    companyFile: 'Αρχείο εταιρείας',
    discussPassage: 'Συζήτηση για αυτό το απόσπασμα',
    contentLabel: 'Περιεχόμενο Δεξιότητας',
    unpublish: 'Κατάργηση δημοσίευσης εταιρικής έκδοσης',
    unpublishQuestion:
      'Να καταργηθεί η δημοσίευση της εταιρικής έκδοσης; Οι συνάδελφοι θα χάσουν την πρόσβαση στις δημοσιευμένες οδηγίες της και οι συνομιλίες και οι agents θα πάψουν να την εφαρμόζουν.',
    confirmUnpublish: 'Επιβεβαίωση κατάργησης δημοσίευσης',
    unpublishing: 'Κατάργηση δημοσίευσης…',
    cancel: 'Ακύρωση',
    unpublished: 'Η δημοσίευση της εταιρικής έκδοσης καταργήθηκε. Τα πρόχειρα δεν άλλαξαν.',
    unpublishFailed: 'Δεν ήταν δυνατή η κατάργηση δημοσίευσης αυτής της Δεξιότητας.',
    newSkillTitle: 'Τι πρέπει να διδάσκει αυτή η Δεξιότητα;',
    newSkillBody:
      'Περιγράψτε πώς γίνεται η εργασία εδώ. Η Poneva συντάσσει το πρόχειρο των οδηγιών από τη γνώση της εταιρείας σας.',
    followedOne: 'Εφαρμόστηκε {count} Δεξιότητα',
    followedMany: 'Εφαρμόστηκαν {count} Δεξιότητες',
    followedSubtitle: 'Διαβάστηκε πριν από αυτή την απάντηση',
    howFound: 'Βρέθηκε για «{need}»',
    howFoundPlain: 'Βρέθηκε για αυτό το αίτημα',
    howPicked: 'Επιλέχθηκε για αυτή τη συνομιλία',
    howAgent: 'Εφαρμόζεται πάντα από αυτόν τον agent',
    howRead: 'Διαβάστηκε για αυτή την απάντηση',
    openFollowed: 'Άνοιγμα {name}, έκδοση {revision}',
    followedEyebrow: 'Δεξιότητα · Έκδοση {revision}',
    followedContext: 'Εφαρμόστηκε για αυτή την απάντηση · Έκδοση {revision}',
    followedRecordNote:
      'Αυτό καταγράφει τι δόθηκε στην Poneva πριν γράψει αυτή την απάντηση, όχι έλεγχο ότι ακολουθήθηκε κάθε βήμα.',
    openInSkills: 'Άνοιγμα στις Δεξιότητες',
    newerVersion: 'Υπάρχει νεότερη έκδοση (Έκδοση {revision}).',
    viewNewer: 'Προβολή έκδοσης {revision}',
    versionUnavailable:
      'Η έκδοση {revision} δεν είναι πλέον διαθέσιμη σε εσάς. Αυτή είναι η τρέχουσα έκδοση.',
    versionLoadFailed:
      'Δεν ήταν δυνατή η φόρτωση της έκδοσης {revision}. Δοκιμάστε ξανά για να δείτε τις οδηγίες που χρησιμοποιήθηκαν για αυτή την απάντηση.',
    retryVersion: 'Δοκιμάστε ξανά',
    lookedFor: 'Αναζητήθηκαν',
    searched: 'Έγινε αναζήτηση',
    searchedSkills: 'Εταιρικές δεξιότητες, με βάση τη γραμμή «Πότε να χρησιμοποιείται»',
    found: 'Βρέθηκαν',
    bestFit: 'Καλύτερη επιλογή',
    noneFound: 'Δεν βρέθηκε δεξιότητα που να ταιριάζει σε αυτήν την αναζήτηση.',
    skillLabel: 'Δεξιότητα',
    versionLabel: 'Έκδοση {revision}',
  },
  hu: {
    pageLead:
      'Így készül itt a jó munka. A Poneva megtalálja a megfelelő készséget bármelyik chatben vagy ügynökben, és a vállalati tudásodra építve követi.',
    search: 'Készségek keresése jelentés alapján…',
    pageNote:
      'Írj és módosíts készségeket chatben. A Poneva mindenhol követi a közzétett készségeket, ahol illenek.',
    cardApplies: 'Akkor érvényes, ha illik',
    cardDraft: 'Piszkozat · Nincs közzétéve',
    cardNewDraft: 'Új piszkozat',
    cardVersion: 'v{revision}',
    emptyTitle: 'Kezdd valamivel, amit gyakran csinálsz.',
    emptyBody:
      'Mondd el a Ponevának, hogyan végzünk itt egy bizonyos típusú munkát. A vállalati tudásodra építve veled együtt megírja az utasításokat, amelyeket minden chat és ügynök követ a közzétételük után.',
    emptyAction: 'Hozd létre az első készségedet',
    draftCard: '{revision}. piszkozat · Utasítások megnyitása',
    versionPublished: '{revision}. verzió · Közzétéve',
    versionDraft: '{revision}. verzió · Piszkozat',
    versionEarlier: '{revision}. verzió · Korábbi',
    publishedVersion: 'A {revision}. verzió van közzétéve.',
    useInChat: 'Használat chatben',
    editInChat: 'Szerkesztés chatben',
    editLatest: 'Legutóbbi szerkesztése chatben',
    applies: 'Érvényes',
    appliesWhenItFits: 'Amikor illik',
    appliesHint:
      'A Poneva a „Mikor használd” sor alapján találja meg bármelyik chatben vagy ügynökfuttatásban.',
    filesRow: 'Fájlok',
    tabInstructions: 'Utasítások',
    tabFollowedIn: 'Követve itt',
    tabVersions: 'Verziók',
    followedInAnswersOne: 'Ezen a héten {count} válaszban követve.',
    followedInAnswersOther: 'Ezen a héten {count} válaszban követve.',
    followedInEmpty:
      'Még nincs követve. Itt jelenik meg, amikor egy chat vagy ügynökfuttatás követi ezt a készséget.',
    followedInFailed: 'Nem sikerült betölteni, hol követték ezt a készséget.',
    versionsCurrent: 'Jelenlegi',
    versionsView: 'Megtekintés',
    restore: 'Visszaállítás új piszkozatként',
    restored: 'A korábbi utasítások új piszkozatként lettek visszaállítva.',
    restoreFailed: 'Ezt a verziót nem sikerült visszaállítani.',
    publishedFor: 'Közzétéve ennek a vállalatnak: {company}.',
    editStaysDraft:
      'A chatben végzett módosítások piszkozatként maradnak, amíg közzé nem teszed őket.',
    draftOnlyYou: 'Ezt a piszkozatot csak te láthatod.',
    draftShared: 'Ezt a piszkozatot csak te és a megosztott személyek láthatják.',
    publishHint: 'Tedd közzé, amikor megfelelően hangzik. Előtte semmit sem kell kipróbálni.',
    newerDraftHint:
      'Tedd közzé ezt a verziót, amikor megfelelően hangzik. Előtte semmit sem kell kipróbálni.',
    publish: 'Közzététel',
    requestPublication: 'Közzététel kérése',
    publishing: 'Közzététel…',
    published: 'Közzétéve a vállalatod számára.',
    requested:
      'Jóváhagyásra elküldve. Egy közzétevő felülvizsgálja ezt a verziót a beérkezett üzenetek között.',
    publishFailed: 'Ezt a verziót nem sikerült közzétenni. Próbáld újra.',
    companyFile: 'Vállalati fájl',
    discussPassage: 'Bekezdés megvitatása',
    contentLabel: 'Készségtartalom',
    unpublish: 'Vállalati verzió visszavonása',
    unpublishQuestion:
      'Visszavonod a vállalati verziót? A munkatársak elveszítik a hozzáférést a közzétett utasításaihoz, a chatek és az ügynökök pedig nem követik többé.',
    confirmUnpublish: 'Visszavonás megerősítése',
    unpublishing: 'Visszavonás…',
    cancel: 'Mégse',
    unpublished: 'A vállalati verzió vissza lett vonva. A piszkozatok nem változtak.',
    unpublishFailed: 'Nem sikerült visszavonni ezt a készséget.',
    newSkillTitle: 'Mit tanítson ez a készség?',
    newSkillBody:
      'Írd le, hogyan végezzük itt a munkát. A Poneva a vállalati tudásodból ezen az oldalon elkészíti az utasítások piszkozatát.',
    followedOne: '{count} követett készség',
    followedMany: '{count} követett készség',
    followedSubtitle: 'A válasz előtt elolvasva',
    howFound: 'A „{need}” alapján találva',
    howFoundPlain: 'Ehhez a kéréshez találva',
    howPicked: 'Ehhez a chathez kiválasztva',
    howAgent: 'Ezt az ügynök mindig követi',
    howRead: 'Ehhez a válaszhoz elolvasva',
    openFollowed: '{name} megnyitása, {revision}. verzió',
    followedEyebrow: 'Készség · {revision}. verzió',
    followedContext: 'Ehhez a válaszhoz követve · {revision}. verzió',
    followedRecordNote:
      'Ez azt rögzíti, hogy mit kapott a Poneva a válasz megírása előtt, nem pedig azt ellenőrzi, hogy minden lépést követett-e.',
    openInSkills: 'Megnyitás a Készségekben',
    newerVersion: 'Újabb verzió érhető el ({revision}. verzió).',
    viewNewer: '{revision}. verzió megtekintése',
    versionUnavailable:
      'A(z) {revision}. verzió már nem érhető el számodra. Ez a jelenlegi verzió.',
    versionLoadFailed:
      'Nem sikerült betölteni a(z) {revision}. verziót. Próbáld újra az ehhez a válaszhoz használt utasítások megtekintéséhez.',
    retryVersion: 'Újrapróbálás',
    lookedFor: 'Keresés erre',
    searched: 'Keresés',
    searchedSkills: 'Vállalati készségek, használat szerint',
    found: 'Találatok',
    bestFit: 'Legjobb egyezés',
    noneFound: 'Ehhez a kereséshez nem található készség.',
    skillLabel: 'Készség',
    versionLabel: 'Verzió: {revision}',
  },
  ga: {
    pageLead:
      'Seo mar a dhéantar dea-obair anseo. Aimsíonn Poneva an Scil is oiriúnaí, in aon chomhrá nó gníomhaire, agus leanann sé í anuas ar eolas do chuideachta.',
    search: 'Aimsigh Scileanna de réir brí…',
    pageNote:
      'Scríobh agus athraigh Scileanna sa chomhrá. Leanann Poneva Scileanna foilsithe cibé áit a n-oireann siad.',
    cardApplies: 'Baineann leis nuair a oireann sé',
    cardDraft: 'Dréacht · Gan foilsiú',
    cardNewDraft: 'Dréacht nua',
    cardVersion: 'v{revision}',
    emptyTitle: 'Tosaigh le rud a dhéanann tú go minic.',
    emptyBody:
      'Inis do Poneva conas a dhéantar cineál oibre anseo. Scríobhann sé na treoracha leat, bunaithe ar eolas do chuideachta, agus leanann gach comhrá agus gníomhaire iad nuair a fhoilsíonn tú iad.',
    emptyAction: 'Cruthaigh do chéad Scil',
    draftCard: 'Dréacht {revision} · Oscail na treoracha',
    versionPublished: 'Leagan {revision} · Foilsithe',
    versionDraft: 'Leagan {revision} · Dréacht',
    versionEarlier: 'Leagan {revision} · Níos luaithe',
    publishedVersion: 'Is é leagan {revision} an leagan foilsithe.',
    useInChat: 'Úsáid sa chomhrá',
    editInChat: 'Cuir in eagar sa chomhrá',
    editLatest: 'Cuir an ceann is déanaí in eagar sa chomhrá',
    applies: 'Baineann leis',
    appliesWhenItFits: 'Nuair a oireann sé',
    appliesHint:
      'Aimsíonn Poneva é de réir a líne ‘Úsáid nuair a’, in aon chomhrá nó rith gníomhaire.',
    filesRow: 'Comhaid',
    tabInstructions: 'Treoracha',
    tabFollowedIn: 'Leanadh é i',
    tabVersions: 'Leaganacha',
    followedInAnswersOne: 'Leanadh é i {count} fhreagra an tseachtain seo.',
    followedInAnswersOther: 'Leanadh é i {count} fhreagra an tseachtain seo.',
    followedInEmpty:
      'Níor leanadh é fós. Nuair a leanann comhrá nó rith gníomhaire an Scil seo, taispeántar anseo é.',
    followedInFailed: 'Níorbh fhéidir na háiteanna inar leanadh an Scil seo a luchtú.',
    versionsCurrent: 'Reatha',
    versionsView: 'Féach',
    restore: 'Athchóirigh mar dhréacht nua',
    restored: 'Athchóiríodh na treoracha níos luaithe mar dhréacht nua.',
    restoreFailed: 'Níorbh fhéidir an leagan seo a athchóiriú.',
    publishedFor: 'Foilsithe do {company}.',
    editStaysDraft:
      'Fanann na hathruithe a dhéanann tú sa chomhrá ina ndréacht go dtí go bhfoilsíonn tú iad.',
    draftOnlyYou: 'Ní féidir ach leatsa an dréacht seo a fheiceáil.',
    draftShared:
      'Ní féidir ach leatsa agus leis na daoine a roinntear leo é an dréacht seo a fheiceáil.',
    publishHint: 'Foilsigh é nuair a léann sé i gceart. Ní gá aon rud a thriail roimh ré.',
    newerDraftHint:
      'Foilsigh an leagan seo nuair a léann sé i gceart. Ní gá aon rud a thriail roimh ré.',
    publish: 'Foilsigh',
    requestPublication: 'Iarr foilsiú',
    publishing: 'Á fhoilsiú…',
    published: 'Foilsithe do do chuideachta.',
    requested:
      'Seolta lena cheadú. Déanann foilsitheoir athbhreithniú ar an leagan seo ina Bhosca Isteach.',
    publishFailed: 'Níorbh fhéidir an leagan seo a fhoilsiú. Bain triail eile as.',
    companyFile: 'Comhad cuideachta',
    discussPassage: 'Pléigh an sliocht seo',
    contentLabel: 'Ábhar na Scile',
    unpublish: 'Dífhoilsigh leagan na cuideachta',
    unpublishQuestion:
      'Dífhoilsigh leagan na cuideachta? Caillfidh comhghleacaithe rochtain ar a threoracha foilsithe, agus scoirfidh comhráite agus gníomhairí dá leanúint.',
    confirmUnpublish: 'Deimhnigh an dífhoilsiú',
    unpublishing: 'Á dhífhoilsiú…',
    cancel: 'Cealaigh',
    unpublished: 'Dífhoilsíodh leagan na cuideachta. Níl aon athrú ar na dréachtaí.',
    unpublishFailed: 'Níorbh fhéidir an Scil seo a dhífhoilsiú.',
    newSkillTitle: 'Cad ba cheart don Scil seo a mhúineadh?',
    newSkillBody:
      'Déan cur síos ar an gcaoi a ndéantar an obair anseo. Dréachtaíonn Poneva na treoracha ar an taobh seo, as eolas do chuideachta.',
    followedOne: 'Leanadh {count} Scil',
    followedMany: 'Leanadh {count} Scil',
    followedSubtitle: 'Léite roimh an bhfreagra seo',
    howFound: 'Aimsithe do “{need}”',
    howFoundPlain: 'Aimsithe don iarratas seo',
    howPicked: 'Roghnaithe don chomhrá seo',
    howAgent: 'Leantar i gcónaí é ag an ngníomhaire seo',
    howRead: 'Léite don fhreagra seo',
    openFollowed: 'Oscail {name}, leagan {revision}',
    followedEyebrow: 'Scil · Leagan {revision}',
    followedContext: 'Leanadh é don fhreagra seo · Leagan {revision}',
    followedRecordNote:
      'Taifeadtar anseo an méid a tugadh do Poneva sular scríobh sí an freagra seo, ní seiceáil go leanadh gach céim.',
    openInSkills: 'Oscail i Scileanna',
    newerVersion: 'Tá leagan níos nuaí ann (Leagan {revision}).',
    viewNewer: 'Féach ar leagan {revision}',
    versionUnavailable: 'Níl leagan {revision} ar fáil duit a thuilleadh. Seo é an leagan reatha.',
    versionLoadFailed:
      'Níorbh fhéidir leagan {revision} a luchtú. Bain triail eile as chun na treoracha a úsáideadh don fhreagra seo a fheiceáil.',
    retryVersion: 'Bain triail eile as',
    lookedFor: 'Lorgadh',
    searched: 'Cuardaithe',
    searchedSkills: 'Scileanna na Cuideachta, de réir a líne “Cathain le húsáid”',
    found: 'Aimsithe',
    bestFit: 'An ceann is fearr',
    noneFound: 'Níor mheaitseáil aon Scil leis an gcuardach seo.',
    skillLabel: 'Scil',
    versionLabel: 'Leagan {revision}',
  },
  it: {
    pageLead:
      'Come si svolge il buon lavoro qui. Poneva trova la Competenza più adatta, in qualsiasi chat o agente, e la segue insieme alle conoscenze della tua azienda.',
    search: 'Trova Competenze per significato…',
    pageNote:
      'Scrivi e modifica le Competenze in chat. Poneva segue le Competenze pubblicate ovunque siano pertinenti.',
    cardApplies: 'Si applica quando è pertinente',
    cardDraft: 'Bozza · Non pubblicata',
    cardNewDraft: 'Nuova bozza',
    cardVersion: 'v{revision}',
    emptyTitle: 'Inizia da qualcosa che fai spesso.',
    emptyBody:
      'Spiega a Poneva come si svolge un tipo di lavoro qui. Scriverà le istruzioni insieme a te, basandosi sulle conoscenze della tua azienda, e ogni chat e agente le seguirà una volta pubblicate.',
    emptyAction: 'Crea la tua prima Competenza',
    draftCard: 'Bozza {revision} · Apri istruzioni',
    versionPublished: 'Versione {revision} · Pubblicata',
    versionDraft: 'Versione {revision} · Bozza',
    versionEarlier: 'Versione {revision} · Precedente',
    publishedVersion: 'La versione {revision} è la versione pubblicata.',
    useInChat: 'Usa in chat',
    editInChat: 'Modifica in chat',
    editLatest: "Modifica l'ultima in chat",
    applies: 'Si applica',
    appliesWhenItFits: 'Quando è pertinente',
    appliesHint:
      'Poneva la trova tramite la riga «Usa quando», in qualsiasi chat o esecuzione di un agente.',
    filesRow: 'File',
    tabInstructions: 'Istruzioni',
    tabFollowedIn: 'Seguita in',
    tabVersions: 'Versioni',
    followedInAnswersOne: 'Seguita in {count} risposta questa settimana.',
    followedInAnswersOther: 'Seguita in {count} risposte questa settimana.',
    followedInEmpty:
      "Non è ancora stata seguita. Quando una chat o un'esecuzione di un agente segue questa Competenza, verrà mostrato qui.",
    followedInFailed: 'Non è stato possibile caricare dove è stata seguita questa Competenza.',
    versionsCurrent: 'Attuale',
    versionsView: 'Visualizza',
    restore: 'Ripristina come nuova bozza',
    restored: 'Le istruzioni precedenti sono state ripristinate come nuova bozza.',
    restoreFailed: 'Non è stato possibile ripristinare questa versione.',
    publishedFor: 'Pubblicata per {company}.',
    editStaysDraft: 'Le modifiche apportate in chat restano una bozza finché non le pubblichi.',
    draftOnlyYou: 'Solo tu puoi vedere questa bozza.',
    draftShared: 'Solo tu e le persone con cui è condivisa potete vedere questa bozza.',
    publishHint: 'Pubblicala quando il testo ti sembra corretto. Non è necessario provarla prima.',
    newerDraftHint:
      'Pubblica questa versione quando il testo ti sembra corretto. Non è necessario provarla prima.',
    publish: 'Pubblica',
    requestPublication: 'Richiedi la pubblicazione',
    publishing: 'Pubblicazione…',
    published: 'Pubblicata per la tua azienda.',
    requested:
      "Inviata per l'approvazione. Un responsabile della pubblicazione esaminerà questa versione nella propria Posta in arrivo.",
    publishFailed: 'Non è stato possibile pubblicare questa versione. Riprova.',
    companyFile: 'File aziendale',
    discussPassage: 'Discuti questo passaggio',
    contentLabel: 'Contenuto della Competenza',
    unpublish: 'Rimuovi la pubblicazione della versione aziendale',
    unpublishQuestion:
      "Rimuovere la pubblicazione della versione aziendale? I colleghi perderanno l'accesso alle istruzioni pubblicate e chat e agenti smetteranno di seguirle.",
    confirmUnpublish: 'Conferma rimozione pubblicazione',
    unpublishing: 'Rimozione della pubblicazione…',
    cancel: 'Annulla',
    unpublished:
      'Pubblicazione della versione aziendale rimossa. Le bozze non sono state modificate.',
    unpublishFailed: 'Non è stato possibile rimuovere la pubblicazione di questa Competenza.',
    newSkillTitle: 'Cosa dovrebbe insegnare questa Competenza?',
    newSkillBody:
      'Descrivi come si svolge il lavoro qui. Poneva prepara le istruzioni da questa parte, basandosi sulle conoscenze della tua azienda.',
    followedOne: '{count} Competenza seguita',
    followedMany: '{count} Competenze seguite',
    followedSubtitle: 'Letta prima di questa risposta',
    howFound: 'Trovata per «{need}»',
    howFoundPlain: 'Trovata per questa richiesta',
    howPicked: 'Scelta per questa chat',
    howAgent: 'Seguita sempre da questo agente',
    howRead: 'Letta per questa risposta',
    openFollowed: 'Apri {name}, versione {revision}',
    followedEyebrow: 'Competenza · Versione {revision}',
    followedContext: 'Seguita per questa risposta · Versione {revision}',
    followedRecordNote:
      'Questo registra ciò che Poneva ha ricevuto prima di scrivere questa risposta, non verifica che ogni passaggio sia stato seguito.',
    openInSkills: 'Apri in Competenze',
    newerVersion: 'Esiste una versione più recente (versione {revision}).',
    viewNewer: 'Visualizza la versione {revision}',
    versionUnavailable:
      'La versione {revision} non è più disponibile per te. Questa è la versione attuale.',
    versionLoadFailed:
      'Non è stato possibile caricare la versione {revision}. Riprova per visualizzare le istruzioni usate per questa risposta.',
    retryVersion: 'Riprova',
    lookedFor: 'Cercato',
    searched: 'Ricercato',
    searchedSkills: 'Competenze aziendali, dalla relativa riga «Usa quando»',
    found: 'Trovato',
    bestFit: 'Corrispondenza migliore',
    noneFound: 'Nessuna competenza corrisponde a questa ricerca.',
    skillLabel: 'Competenza',
    versionLabel: 'Versione {revision}',
  },
  lv: {
    pageLead:
      'Kā šeit paveikt labu darbu. Poneva jebkurā tērzēšanā vai aģentā atrod piemēroto prasmi un izmanto to kopā ar jūsu uzņēmuma zināšanām.',
    search: 'Atrast prasmes pēc nozīmes…',
    pageNote:
      'Rakstiet un mainiet prasmes tērzēšanā. Poneva izmanto publicētās prasmes visur, kur tās ir piemērotas.',
    cardApplies: 'Izmanto, kad piemērota',
    cardDraft: 'Melnraksts · Nav publicēts',
    cardNewDraft: 'Jauns melnraksts',
    cardVersion: 'v{revision}',
    emptyTitle: 'Sāciet ar kaut ko, ko darāt bieži.',
    emptyBody:
      'Pastāstiet Poneva, kā šeit tiek paveikts noteikta veida darbs. Tā kopā ar jums, balstoties uz jūsu uzņēmuma zināšanām, uzrakstīs instrukcijas, un pēc publicēšanas tās izmantos katrā tērzēšanā un aģentā.',
    emptyAction: 'Izveidot savu pirmo prasmi',
    draftCard: 'Melnraksts {revision} · Atvērt instrukcijas',
    versionPublished: 'Versija {revision} · Publicēta',
    versionDraft: 'Versija {revision} · Melnraksts',
    versionEarlier: 'Versija {revision} · Iepriekšēja',
    publishedVersion: 'Versija {revision} ir publicētā versija.',
    useInChat: 'Izmantot tērzēšanā',
    editInChat: 'Rediģēt tērzēšanā',
    editLatest: 'Rediģēt jaunāko tērzēšanā',
    applies: 'Izmanto',
    appliesWhenItFits: 'Kad piemērota',
    appliesHint:
      'Poneva to atrod pēc rindas “Izmantot, kad”, jebkurā tērzēšanā vai aģenta izpildē.',
    filesRow: 'Faili',
    tabInstructions: 'Instrukcijas',
    tabFollowedIn: 'Izmantota',
    tabVersions: 'Versijas',
    followedInAnswersOne: 'Šonedēļ izmantota {count} atbildē.',
    followedInAnswersOther: 'Šonedēļ izmantota {count} atbildēs.',
    followedInEmpty:
      'Vēl nav izmantota. Kad tērzēšanā vai aģenta izpildē tiks izmantota šī prasme, tā būs redzama šeit.',
    followedInFailed: 'Neizdevās ielādēt, kur šī prasme tika izmantota.',
    versionsCurrent: 'Pašreizējā',
    versionsView: 'Skatīt',
    restore: 'Atjaunot kā jaunu melnrakstu',
    restored: 'Iepriekšējās instrukcijas atjaunotas kā jauns melnraksts.',
    restoreFailed: 'Šo versiju neizdevās atjaunot.',
    publishedFor: 'Publicēta uzņēmumam {company}.',
    editStaysDraft: 'Tērzēšanā veiktās izmaiņas paliek kā melnraksts, līdz tās publicējat.',
    draftOnlyYou: 'Šo melnrakstu varat redzēt tikai jūs.',
    draftShared: 'Šo melnrakstu varat redzēt tikai jūs un personas, ar kurām tas ir kopīgots.',
    publishHint: 'Publicējiet, kad teksts ir gatavs. Pirms tam nekas nav jāizmēģina.',
    newerDraftHint: 'Publicējiet šo versiju, kad teksts ir gatavs. Pirms tam nekas nav jāizmēģina.',
    publish: 'Publicēt',
    requestPublication: 'Pieprasīt publicēšanu',
    publishing: 'Publicē…',
    published: 'Publicēta jūsu uzņēmumam.',
    requested: 'Nosūtīts apstiprināšanai. Publicētājs šo versiju pārskatīs savā iesūtnē.',
    publishFailed: 'Šo versiju neizdevās publicēt. Mēģiniet vēlreiz.',
    companyFile: 'Uzņēmuma fails',
    discussPassage: 'Apspriest šo fragmentu',
    contentLabel: 'Prasmes saturs',
    unpublish: 'Atcelt uzņēmuma versijas publicēšanu',
    unpublishQuestion:
      'Atcelt uzņēmuma versijas publicēšanu? Kolēģi zaudēs piekļuvi tās publicētajām instrukcijām, un tērzēšanas un aģenti vairs to neizmantos.',
    confirmUnpublish: 'Apstiprināt publicēšanas atcelšanu',
    unpublishing: 'Tiek atcelta publicēšana…',
    cancel: 'Atcelt',
    unpublished: 'Uzņēmuma versijas publicēšana atcelta. Melnraksti nav mainīti.',
    unpublishFailed: 'Neizdevās atcelt šīs prasmes publicēšanu.',
    newSkillTitle: 'Ko šai prasmei vajadzētu iemācīt?',
    newSkillBody:
      'Aprakstiet, kā šeit tiek paveikts darbs. Poneva šajā pusē, balstoties uz jūsu uzņēmuma zināšanām, sagatavos instrukciju melnrakstu.',
    followedOne: 'Izmantota {count} prasme',
    followedMany: 'Izmantotas {count} prasmes',
    followedSubtitle: 'Izlasīta pirms šīs atbildes',
    howFound: 'Atrasta vaicājumam “{need}”',
    howFoundPlain: 'Atrasta šim pieprasījumam',
    howPicked: 'Atlasīta šai tērzēšanai',
    howAgent: 'Šis aģents to izmanto vienmēr',
    howRead: 'Izlasīta šai atbildei',
    openFollowed: 'Atvērt {name}, versija {revision}',
    followedEyebrow: 'Prasme · Versija {revision}',
    followedContext: 'Izmantota šai atbildei · Versija {revision}',
    followedRecordNote:
      'Šeit tiek reģistrēts, ko Poneva saņēma pirms šīs atbildes uzrakstīšanas, nevis pārbaudīts, vai tika izpildīts katrs solis.',
    openInSkills: 'Atvērt prasmēs',
    newerVersion: 'Ir pieejama jaunāka versija (versija {revision}).',
    viewNewer: 'Skatīt versiju {revision}',
    versionUnavailable: 'Versija {revision} jums vairs nav pieejama. Šī ir pašreizējā versija.',
    versionLoadFailed:
      'Neizdevās ielādēt versiju {revision}. Mēģiniet vēlreiz, lai skatītu šai atbildei izmantotās instrukcijas.',
    retryVersion: 'Mēģināt vēlreiz',
    lookedFor: 'Meklēts',
    searched: 'Meklēts',
    searchedSkills: 'Uzņēmuma prasmes pēc to “Izmanto, kad” rindiņas',
    found: 'Atrasts',
    bestFit: 'Labākā atbilstība',
    noneFound: 'Neviena prasme neatbilda šai meklēšanai.',
    skillLabel: 'Prasme',
    versionLabel: 'Versija {revision}',
  },
  lt: {
    pageLead:
      'Kaip čia atliekamas geras darbas. Poneva randa tinkamą Įgūdį bet kuriame pokalbyje ar agente ir taiko jį kartu su jūsų įmonės žiniomis.',
    search: 'Rasti Įgūdžius pagal prasmę…',
    pageNote:
      'Rašykite ir keiskite Įgūdžius pokalbyje. Poneva taiko paskelbtus Įgūdžius visur, kur jie tinka.',
    cardApplies: 'Taikoma, kai tinka',
    cardDraft: 'Juodraštis · Nepaskelbta',
    cardNewDraft: 'Naujas juodraštis',
    cardVersion: 'v{revision}',
    emptyTitle: 'Pradėkite nuo to, ką dažnai darote.',
    emptyBody:
      'Papasakokite Ponevai, kaip čia atliekamas tam tikras darbas. Ji kartu su jumis, remdamasi jūsų įmonės žiniomis, parengs instrukcijas, o jas paskelbus jų laikysis visi pokalbiai ir agentai.',
    emptyAction: 'Sukurti pirmąjį Įgūdį',
    draftCard: 'Juodraštis {revision} · Atidaryti instrukcijas',
    versionPublished: 'Versija {revision} · Paskelbta',
    versionDraft: 'Versija {revision} · Juodraštis',
    versionEarlier: 'Versija {revision} · Ankstesnė',
    publishedVersion: 'Paskelbta versija yra {revision} versija.',
    useInChat: 'Naudoti pokalbyje',
    editInChat: 'Redaguoti pokalbyje',
    editLatest: 'Redaguoti naujausią pokalbyje',
    applies: 'Taikoma',
    appliesWhenItFits: 'Kai tinka',
    appliesHint:
      'Poneva randa jį pagal eilutę „Kada naudoti“ bet kuriame pokalbyje ar agento vykdyme.',
    filesRow: 'Failai',
    tabInstructions: 'Instrukcijos',
    tabFollowedIn: 'Taikyta',
    tabVersions: 'Versijos',
    followedInAnswersOne: 'Šią savaitę taikyta {count} atsakyme.',
    followedInAnswersOther: 'Šią savaitę taikyta {count} atsakymuose.',
    followedInEmpty:
      'Dar netaikyta. Čia bus rodoma, kai pokalbis ar agento vykdymas pritaikys šį Įgūdį.',
    followedInFailed: 'Nepavyko įkelti vietų, kuriose šis Įgūdis buvo pritaikytas.',
    versionsCurrent: 'Dabartinė',
    versionsView: 'Peržiūrėti',
    restore: 'Atkurti kaip naują juodraštį',
    restored: 'Ankstesnės instrukcijos atkurtos kaip naujas juodraštis.',
    restoreFailed: 'Nepavyko atkurti šios versijos.',
    publishedFor: 'Paskelbta įmonei {company}.',
    editStaysDraft: 'Pokalbyje atlikti pakeitimai lieka juodraščiu, kol juos paskelbsite.',
    draftOnlyYou: 'Šį juodraštį matote tik jūs.',
    draftShared: 'Šį juodraštį galite matyti tik jūs ir žmonės, su kuriais jis bendrinamas.',
    publishHint: 'Paskelbkite, kai tekstas bus tinkamas. Pirmiausia nieko išbandyti nereikia.',
    newerDraftHint:
      'Paskelbkite šią versiją, kai tekstas bus tinkamas. Pirmiausia nieko išbandyti nereikia.',
    publish: 'Paskelbti',
    requestPublication: 'Prašyti paskelbti',
    publishing: 'Skelbiama…',
    published: 'Paskelbta jūsų įmonei.',
    requested: 'Išsiųsta tvirtinti. Skelbėjas peržiūrės šią versiją savo gautuosiuose.',
    publishFailed: 'Nepavyko paskelbti šios versijos. Bandykite dar kartą.',
    companyFile: 'Įmonės failas',
    discussPassage: 'Aptarti šią ištrauką',
    contentLabel: 'Įgūdžio turinys',
    unpublish: 'Nebeskelbti įmonės versijos',
    unpublishQuestion:
      'Nebeskelbti įmonės versijos? Kolegos nebeturės prieigos prie paskelbtų instrukcijų, o pokalbiai ir agentai nustos jų laikytis.',
    confirmUnpublish: 'Patvirtinti nebeskelbimą',
    unpublishing: 'Nebeskelbiama…',
    cancel: 'Atšaukti',
    unpublished: 'Įmonės versija nebeskelbiama. Juodraščiai nepakeisti.',
    unpublishFailed: 'Nepavyko nebeskelbti šio Įgūdžio.',
    newSkillTitle: 'Ko turėtų mokyti šis Įgūdis?',
    newSkillBody:
      'Apibūdinkite, kaip čia atliekamas darbas. Poneva šioje pusėje, remdamasi jūsų įmonės žiniomis, parengs instrukcijų juodraštį.',
    followedOne: 'Pritaikytas {count} Įgūdis',
    followedMany: 'Pritaikyta {count} Įgūdžių',
    followedSubtitle: 'Perskaityta prieš šį atsakymą',
    howFound: 'Rasta pagal „{need}“',
    howFoundPlain: 'Rasta pagal šį prašymą',
    howPicked: 'Parinkta šiam pokalbiui',
    howAgent: 'Šis agentas visada jo laikosi',
    howRead: 'Perskaityta rengiant šį atsakymą',
    openFollowed: 'Atidaryti {name}, {revision} versiją',
    followedEyebrow: 'Įgūdis · {revision} versija',
    followedContext: 'Pritaikyta rengiant šį atsakymą · {revision} versija',
    followedRecordNote:
      'Tai nurodo, kas buvo pateikta Ponevai prieš parašant šį atsakymą, bet nepatvirtina, kad buvo laikomasi kiekvieno veiksmo.',
    openInSkills: 'Atidaryti skiltyje „Įgūdžiai“',
    newerVersion: 'Yra naujesnė versija ({revision} versija).',
    viewNewer: 'Peržiūrėti {revision} versiją',
    versionUnavailable: 'Versija {revision} jums nebepasiekiama. Tai dabartinė versija.',
    versionLoadFailed:
      'Nepavyko įkelti {revision} versijos. Bandykite dar kartą, kad pamatytumėte šiam atsakymui naudotas instrukcijas.',
    retryVersion: 'Bandyti dar kartą',
    lookedFor: 'Ieškota',
    searched: 'Ieškota',
    searchedSkills: 'Įmonės įgūdžiai pagal eilutę „Kada naudoti“',
    found: 'Rasta',
    bestFit: 'Geriausiai tinkantis',
    noneFound: 'Paiešką atitinkančių įgūdžių nerasta.',
    skillLabel: 'Įgūdis',
    versionLabel: 'Versija {revision}',
  },
  mt: {
    pageLead:
      'Kif isir xogħol tajjeb hawn. Poneva jsib il-Ħila xierqa, fi kwalunkwe chat jew aġent, u jsegwiha flimkien mal-għarfien tal-kumpanija tiegħek.',
    search: 'Sib Ħiliet skont it-tifsira…',
    pageNote: 'Ikteb u biddel il-Ħiliet fiċ-chat. Poneva jsegwihom kull fejn ikunu xierqa.',
    cardApplies: 'Tapplika meta tkun xierqa',
    cardDraft: 'Abbozz · Mhux ippubblikata',
    cardNewDraft: 'Abbozz ġdid',
    cardVersion: 'v{revision}',
    emptyTitle: 'Ibda b’xi ħaġa li tagħmel spiss.',
    emptyBody:
      'Għid lil Poneva kif isir tip ta’ xogħol hawn. Huwa jikteb l-istruzzjonijiet miegħek, fuq l-għarfien tal-kumpanija tiegħek, u kull chat u aġent isegwihom ladarba tippubblikahom.',
    emptyAction: 'Oħloq l-ewwel Ħila tiegħek',
    draftCard: 'Abbozz {revision} · Iftaħ l-istruzzjonijiet',
    versionPublished: 'Verżjoni {revision} · Ippubblikata',
    versionDraft: 'Verżjoni {revision} · Abbozz',
    versionEarlier: 'Verżjoni {revision} · Preċedenti',
    publishedVersion: 'Il-verżjoni {revision} hija l-verżjoni ppubblikata.',
    useInChat: 'Uża fiċ-chat',
    editInChat: 'Editja fiċ-chat',
    editLatest: 'Editja l-aħħar waħda fiċ-chat',
    applies: 'Tapplika',
    appliesWhenItFits: 'Meta tkun xierqa',
    appliesHint:
      'Poneva jsibha permezz tal-linja “Uża meta”, fi kwalunkwe chat jew eżekuzzjoni ta’ aġent.',
    filesRow: 'Fajls',
    tabInstructions: 'Istruzzjonijiet',
    tabFollowedIn: 'Segwita f’',
    tabVersions: 'Verżjonijiet',
    followedInAnswersOne: 'Segwita f’{count} tweġiba din il-ġimgħa.',
    followedInAnswersOther: 'Segwita f’{count} tweġibiet din il-ġimgħa.',
    followedInEmpty:
      'Għadha ma ġietx segwita. Meta chat jew eżekuzzjoni ta’ aġent isegwi din il-Ħila, tidher hawn.',
    followedInFailed: 'Ma setax jitgħabba fejn ġiet segwita din il-Ħila.',
    versionsCurrent: 'Kurrenti',
    versionsView: 'Ara',
    restore: 'Irrestawra bħala abbozz ġdid',
    restored: 'L-istruzzjonijiet preċedenti ġew irrestawrati bħala abbozz ġdid.',
    restoreFailed: 'Din il-verżjoni ma setgħetx tiġi rrestawrata.',
    publishedFor: 'Ippubblikata għal {company}.',
    editStaysDraft: 'Il-bidliet li tagħmel fiċ-chat jibqgħu abbozz sakemm tippubblikahom.',
    draftOnlyYou: 'Int biss tista’ tara dan l-abbozz.',
    draftShared: 'Int u n-nies li magħhom huwa maqsum biss tistgħu taraw dan l-abbozz.',
    publishHint: 'Ippubblika meta l-kliem ikun tajjeb. M’hemmx għalfejn jiġi ppruvat l-ewwel.',
    newerDraftHint:
      'Ippubblika din il-verżjoni meta l-kliem ikun tajjeb. M’hemmx għalfejn tiġi ppruvata l-ewwel.',
    publish: 'Ippubblika',
    requestPublication: 'Itlob il-pubblikazzjoni',
    publishing: 'Qed tiġi ppubblikata…',
    published: 'Ippubblikata għall-kumpanija tiegħek.',
    requested:
      'Intbagħtet għall-approvazzjoni. Pubblikatur jirrevedi din il-verżjoni fl-Inbox tiegħu.',
    publishFailed: 'Din il-verżjoni ma setgħetx tiġi ppubblikata. Erġa’ pprova.',
    companyFile: 'Fajl tal-kumpanija',
    discussPassage: 'Iddiskuti din is-silta',
    contentLabel: 'Kontenut tal-Ħila',
    unpublish: 'Neħħi l-pubblikazzjoni tal-verżjoni tal-kumpanija',
    unpublishQuestion:
      'Tneħħi l-pubblikazzjoni tal-verżjoni tal-kumpanija? Il-kollegi jitilfu l-aċċess għall-istruzzjonijiet ippubblikati tagħha, u ċ-chats u l-aġenti jieqfu jsegwuha.',
    confirmUnpublish: 'Ikkonferma t-tneħħija tal-pubblikazzjoni',
    unpublishing: 'Qed titneħħa l-pubblikazzjoni…',
    cancel: 'Ikkanċella',
    unpublished: 'Il-pubblikazzjoni tal-verżjoni tal-kumpanija tneħħiet. L-abbozzi ma nbidlux.',
    unpublishFailed: 'Ma setgħetx titneħħa l-pubblikazzjoni ta’ din il-Ħila.',
    newSkillTitle: 'X’għandha tgħallem din il-Ħila?',
    newSkillBody:
      'Iddeskrivi kif isir ix-xogħol hawn. Poneva jħejji l-istruzzjonijiet fuq din in-naħa, mill-għarfien tal-kumpanija tiegħek.',
    followedOne: 'Ħila segwita: {count}',
    followedMany: 'Ħiliet segwiti: {count}',
    followedSubtitle: 'Aqra qabel din it-tweġiba',
    howFound: 'Instabet għal “{need}”',
    howFoundPlain: 'Instabet għal din it-talba',
    howPicked: 'Magħżula għal dan iċ-chat',
    howAgent: 'Dejjem segwita minn dan l-aġent',
    howRead: 'Inqrat għal din it-tweġiba',
    openFollowed: 'Iftaħ {name}, verżjoni {revision}',
    followedEyebrow: 'Ħila · Verżjoni {revision}',
    followedContext: 'Segwita għal din it-tweġiba · Verżjoni {revision}',
    followedRecordNote:
      'Dan jirreġistra dak li ngħata lil Poneva qabel ma kiteb din it-tweġiba, mhux verifika li kull pass ġie segwit.',
    openInSkills: 'Iftaħ fil-Ħiliet',
    newerVersion: 'Teżisti verżjoni aktar ġdida (Verżjoni {revision}).',
    viewNewer: 'Ara l-verżjoni {revision}',
    versionUnavailable:
      'Il-verżjoni {revision} m’għadhiex disponibbli għalik. Din hija l-verżjoni kurrenti.',
    versionLoadFailed:
      'Ma setgħetx titgħabba l-verżjoni {revision}. Erġa’ pprova biex tara l-istruzzjonijiet użati għal din it-tweġiba.',
    retryVersion: 'Erġa’ pprova',
    lookedFor: 'Fittex għal',
    searched: 'Imfittex',
    searchedSkills: 'Ħiliet tal-kumpanija, skont il-linja “Uża meta”',
    found: 'Instab',
    bestFit: 'L-aħjar qbil',
    noneFound: 'Ma nstab l-ebda Skill li jaqbel ma’ din it-tfittxija.',
    skillLabel: 'Skill',
    versionLabel: 'Verżjoni {revision}',
  },
  pl: {
    pageLead:
      'Jak tutaj powstaje dobra praca. Poneva znajduje odpowiednią umiejętność w dowolnym czacie lub agencie i stosuje ją wraz z wiedzą firmową.',
    search: 'Znajdź umiejętności według znaczenia…',
    pageNote:
      'Pisz i zmieniaj umiejętności na czacie. Poneva stosuje opublikowane umiejętności wszędzie tam, gdzie pasują.',
    cardApplies: 'Stosowana, gdy pasuje',
    cardDraft: 'Wersja robocza · Nieopublikowana',
    cardNewDraft: 'Nowa wersja robocza',
    cardVersion: 'wersja {revision}',
    emptyTitle: 'Zacznij od czegoś, co często robisz.',
    emptyBody:
      'Opowiedz Ponevie, jak wykonuje się tutaj określony rodzaj pracy. Wspólnie z Tobą spisze instrukcje na podstawie wiedzy firmowej, a każdy czat i agent będzie ich przestrzegać po ich opublikowaniu.',
    emptyAction: 'Utwórz pierwszą umiejętność',
    draftCard: 'Wersja robocza {revision} · Otwórz instrukcje',
    versionPublished: 'Wersja {revision} · Opublikowana',
    versionDraft: 'Wersja {revision} · Wersja robocza',
    versionEarlier: 'Wersja {revision} · Wcześniejsza',
    publishedVersion: 'Wersja {revision} jest opublikowaną wersją.',
    useInChat: 'Użyj na czacie',
    editInChat: 'Edytuj na czacie',
    editLatest: 'Edytuj najnowszą na czacie',
    applies: 'Stosowanie',
    appliesWhenItFits: 'Gdy pasuje',
    appliesHint:
      'Poneva znajduje ją na podstawie wiersza „Użyj, gdy”, w dowolnym czacie lub podczas działania agenta.',
    filesRow: 'Pliki',
    tabInstructions: 'Instrukcje',
    tabFollowedIn: 'Zastosowania',
    tabVersions: 'Wersje',
    followedInAnswersOne: 'Zastosowano w {count} odpowiedzi w tym tygodniu.',
    followedInAnswersOther: 'Zastosowano w {count} odpowiedziach w tym tygodniu.',
    followedInEmpty:
      'Jeszcze nie zastosowano. Gdy czat lub działanie agenta zastosuje tę umiejętność, pojawi się tutaj.',
    followedInFailed: 'Nie udało się załadować informacji o tym, gdzie zastosowano tę umiejętność.',
    versionsCurrent: 'Bieżąca',
    versionsView: 'Wyświetl',
    restore: 'Przywróć jako nową wersję roboczą',
    restored: 'Wcześniejsze instrukcje przywrócono jako nową wersję roboczą.',
    restoreFailed: 'Nie udało się przywrócić tej wersji.',
    publishedFor: 'Opublikowano dla firmy {company}.',
    editStaysDraft:
      'Zmiany wprowadzone na czacie pozostają wersją roboczą, dopóki ich nie opublikujesz.',
    draftOnlyYou: 'Tylko Ty możesz zobaczyć tę wersję roboczą.',
    draftShared: 'Tylko Ty i osoby, którym ją udostępniono, możecie zobaczyć tę wersję roboczą.',
    publishHint:
      'Opublikuj, gdy treść będzie brzmiała właściwie. Nie trzeba jej wcześniej wypróbowywać.',
    newerDraftHint:
      'Opublikuj tę wersję, gdy treść będzie brzmiała właściwie. Nie trzeba jej wcześniej wypróbowywać.',
    publish: 'Opublikuj',
    requestPublication: 'Poproś o publikację',
    publishing: 'Publikowanie…',
    published: 'Opublikowano dla firmy.',
    requested:
      'Wysłano do zatwierdzenia. Osoba publikująca sprawdzi tę wersję w swojej skrzynce odbiorczej.',
    publishFailed: 'Nie udało się opublikować tej wersji. Spróbuj ponownie.',
    companyFile: 'Plik firmowy',
    discussPassage: 'Omów ten fragment',
    contentLabel: 'Treść umiejętności',
    unpublish: 'Wycofaj wersję firmową z publikacji',
    unpublishQuestion:
      'Wycofać wersję firmową z publikacji? Współpracownicy stracą dostęp do jej opublikowanych instrukcji, a czaty i agenci przestaną jej przestrzegać.',
    confirmUnpublish: 'Potwierdź wycofanie z publikacji',
    unpublishing: 'Wycofywanie z publikacji…',
    cancel: 'Anuluj',
    unpublished:
      'Wersja firmowa została wycofana z publikacji. Wersje robocze pozostały bez zmian.',
    unpublishFailed: 'Nie udało się wycofać tej umiejętności z publikacji.',
    newSkillTitle: 'Czego powinna uczyć ta umiejętność?',
    newSkillBody:
      'Opisz, jak wykonuje się tutaj tę pracę. Poneva przygotuje instrukcje po tej stronie na podstawie wiedzy firmowej.',
    followedOne: 'Zastosowano {count} umiejętność',
    followedMany: 'Zastosowano {count} umiejętności',
    followedSubtitle: 'Przeczytano przed tą odpowiedzią',
    howFound: 'Znaleziono dla „{need}”',
    howFoundPlain: 'Znaleziono dla tego zapytania',
    howPicked: 'Wybrano dla tego czatu',
    howAgent: 'Ten agent zawsze jej przestrzega',
    howRead: 'Przeczytano na potrzeby tej odpowiedzi',
    openFollowed: 'Otwórz {name}, wersja {revision}',
    followedEyebrow: 'Umiejętność · Wersja {revision}',
    followedContext: 'Zastosowano w tej odpowiedzi · Wersja {revision}',
    followedRecordNote:
      'To zapis tego, co przekazano Ponevie przed napisaniem tej odpowiedzi, a nie potwierdzenie, że wykonano każdy krok.',
    openInSkills: 'Otwórz w sekcji Umiejętności',
    newerVersion: 'Dostępna jest nowsza wersja (wersja {revision}).',
    viewNewer: 'Wyświetl wersję {revision}',
    versionUnavailable:
      'Wersja {revision} nie jest już dla Ciebie dostępna. To jest bieżąca wersja.',
    versionLoadFailed:
      'Nie udało się załadować wersji {revision}. Spróbuj ponownie, aby zobaczyć instrukcje użyte w tej odpowiedzi.',
    retryVersion: 'Spróbuj ponownie',
    lookedFor: 'Wyszukano',
    searched: 'Przeszukano',
    searchedSkills: 'Umiejętności firmy według wiersza „Kiedy używać”',
    found: 'Znaleziono',
    bestFit: 'Najlepsze dopasowanie',
    noneFound: 'Żadna umiejętność nie pasuje do tego wyszukiwania.',
    skillLabel: 'Umiejętność',
    versionLabel: 'Wersja {revision}',
  },
  pt: {
    pageLead:
      'Como o bom trabalho é feito aqui. A Poneva encontra a habilidade certa, em qualquer chat ou agente, e a aplica com base no conhecimento da sua empresa.',
    search: 'Encontrar habilidades por significado…',
    pageNote:
      'Escreva e altere habilidades no chat. A Poneva aplica habilidades publicadas sempre que forem pertinentes.',
    cardApplies: 'Aplica-se quando for pertinente',
    cardDraft: 'Rascunho · Não publicada',
    cardNewDraft: 'Novo rascunho',
    cardVersion: 'v{revision}',
    emptyTitle: 'Comece com algo que você faz com frequência.',
    emptyBody:
      'Diga à Poneva como um tipo de trabalho é feito aqui. Ela escreve as instruções com você, com base no conhecimento da sua empresa, e todos os chats e agentes as aplicam depois que você publicar.',
    emptyAction: 'Criar sua primeira habilidade',
    draftCard: 'Rascunho {revision} · Abrir instruções',
    versionPublished: 'Versão {revision} · Publicada',
    versionDraft: 'Versão {revision} · Rascunho',
    versionEarlier: 'Versão {revision} · Anterior',
    publishedVersion: 'A versão {revision} é a versão publicada.',
    useInChat: 'Usar no chat',
    editInChat: 'Editar no chat',
    editLatest: 'Editar a mais recente no chat',
    applies: 'Aplica-se',
    appliesWhenItFits: 'Quando for pertinente',
    appliesHint:
      'A Poneva encontra a habilidade pela linha “Usar quando”, em qualquer chat ou execução de agente.',
    filesRow: 'Arquivos',
    tabInstructions: 'Instruções',
    tabFollowedIn: 'Aplicada em',
    tabVersions: 'Versões',
    followedInAnswersOne: 'Aplicada em {count} resposta nesta semana.',
    followedInAnswersOther: 'Aplicada em {count} respostas nesta semana.',
    followedInEmpty:
      'Ainda não foi aplicada. Quando um chat ou uma execução de agente aplicar esta habilidade, ela aparecerá aqui.',
    followedInFailed: 'Não foi possível carregar onde esta habilidade foi aplicada.',
    versionsCurrent: 'Atual',
    versionsView: 'Ver',
    restore: 'Restaurar como novo rascunho',
    restored: 'As instruções anteriores foram restauradas como um novo rascunho.',
    restoreFailed: 'Não foi possível restaurar esta versão.',
    publishedFor: 'Publicada para {company}.',
    editStaysDraft: 'As alterações feitas no chat permanecem como rascunho até serem publicadas.',
    draftOnlyYou: 'Somente você pode ver este rascunho.',
    draftShared:
      'Somente você e as pessoas com quem ele foi compartilhado podem ver este rascunho.',
    publishHint: 'Publique quando o texto estiver adequado. Não é preciso testá-lo antes.',
    newerDraftHint:
      'Publique esta versão quando o texto estiver adequado. Não é preciso testá-la antes.',
    publish: 'Publicar',
    requestPublication: 'Solicitar publicação',
    publishing: 'Publicando…',
    published: 'Publicada para sua empresa.',
    requested: 'Enviada para aprovação. Um publicador analisa esta versão na caixa de entrada.',
    publishFailed: 'Não foi possível publicar esta versão. Tente novamente.',
    companyFile: 'Arquivo da empresa',
    discussPassage: 'Discutir este trecho',
    contentLabel: 'Conteúdo da habilidade',
    unpublish: 'Cancelar publicação da versão da empresa',
    unpublishQuestion:
      'Cancelar a publicação da versão da empresa? Os colegas perderão acesso às instruções publicadas, e os chats e agentes deixarão de aplicá-las.',
    confirmUnpublish: 'Confirmar cancelamento da publicação',
    unpublishing: 'Cancelando publicação…',
    cancel: 'Cancelar',
    unpublished:
      'A publicação da versão da empresa foi cancelada. Os rascunhos não foram alterados.',
    unpublishFailed: 'Não foi possível cancelar a publicação desta habilidade.',
    newSkillTitle: 'O que esta habilidade deve ensinar?',
    newSkillBody:
      'Descreva como o trabalho é feito aqui. A Poneva prepara as instruções deste lado, com base no conhecimento da sua empresa.',
    followedOne: '{count} habilidade aplicada',
    followedMany: '{count} habilidades aplicadas',
    followedSubtitle: 'Lida antes desta resposta',
    howFound: 'Encontrada para “{need}”',
    howFoundPlain: 'Encontrada para esta solicitação',
    howPicked: 'Selecionada para este chat',
    howAgent: 'Sempre aplicada por este agente',
    howRead: 'Lida para esta resposta',
    openFollowed: 'Abrir {name}, versão {revision}',
    followedEyebrow: 'Habilidade · Versão {revision}',
    followedContext: 'Aplicada para esta resposta · Versão {revision}',
    followedRecordNote:
      'Isso registra o que a Poneva recebeu antes de escrever esta resposta, não uma verificação de que todas as etapas foram seguidas.',
    openInSkills: 'Abrir em Habilidades',
    newerVersion: 'Existe uma versão mais recente (versão {revision}).',
    viewNewer: 'Ver a versão {revision}',
    versionUnavailable:
      'A versão {revision} não está mais disponível para você. Esta é a versão atual.',
    versionLoadFailed:
      'Não foi possível carregar a versão {revision}. Tente novamente para ver as instruções usadas nesta resposta.',
    retryVersion: 'Tentar novamente',
    lookedFor: 'Procurado',
    searched: 'Pesquisado',
    searchedSkills: 'Habilidades da empresa, por sua finalidade',
    found: 'Encontrado',
    bestFit: 'Melhor correspondência',
    noneFound: 'Nenhuma habilidade correspondeu a esta pesquisa.',
    skillLabel: 'Habilidade',
    versionLabel: 'Versão {revision}',
  },
  ro: {
    pageLead:
      'Așa se face treabă bună aici. Poneva găsește competența potrivită, în orice chat sau agent, și o urmează pe baza cunoștințelor companiei.',
    search: 'Găsește competențe după semnificație…',
    pageNote:
      'Scrie și modifică competențe în chat. Poneva urmează competențele publicate oriunde se potrivesc.',
    cardApplies: 'Se aplică atunci când se potrivește',
    cardDraft: 'Ciornă · Nepublicată',
    cardNewDraft: 'Ciornă nouă',
    cardVersion: 'v{revision}',
    emptyTitle: 'Începe cu ceva ce faci des.',
    emptyBody:
      'Spune-i lui Poneva cum se face un anumit tip de muncă aici. Îți scrie instrucțiunile, folosind cunoștințele companiei, iar fiecare chat și agent le urmează după ce le publici.',
    emptyAction: 'Creează prima competență',
    draftCard: 'Ciornă {revision} · Deschide instrucțiunile',
    versionPublished: 'Versiunea {revision} · Publicată',
    versionDraft: 'Versiunea {revision} · Ciornă',
    versionEarlier: 'Versiunea {revision} · Anterioară',
    publishedVersion: 'Versiunea {revision} este versiunea publicată.',
    useInChat: 'Folosește în chat',
    editInChat: 'Editează în chat',
    editLatest: 'Editează ultima versiune în chat',
    applies: 'Se aplică',
    appliesWhenItFits: 'Când se potrivește',
    appliesHint: 'Poneva o găsește după linia „Folosește când”, în orice chat sau rulare de agent.',
    filesRow: 'Fișiere',
    tabInstructions: 'Instrucțiuni',
    tabFollowedIn: 'Urmată în',
    tabVersions: 'Versiuni',
    followedInAnswersOne: 'Urmată în {count} răspuns în această săptămână.',
    followedInAnswersOther: 'Urmată în {count} răspunsuri în această săptămână.',
    followedInEmpty:
      'Nu a fost urmată încă. Când un chat sau o rulare de agent urmează această competență, va apărea aici.',
    followedInFailed: 'Locurile în care a fost urmată această competență nu au putut fi încărcate.',
    versionsCurrent: 'Actuală',
    versionsView: 'Vezi',
    restore: 'Restabilește ca ciornă nouă',
    restored: 'Instrucțiunile anterioare au fost restabilite ca o ciornă nouă.',
    restoreFailed: 'Această versiune nu a putut fi restabilită.',
    publishedFor: 'Publicată pentru {company}.',
    editStaysDraft: 'Modificările făcute în chat rămân ciornă până când le publici.',
    draftOnlyYou: 'Doar tu poți vedea această ciornă.',
    draftShared: 'Doar tu și persoanele cu care este partajată o puteți vedea.',
    publishHint: 'Publică atunci când formularea este potrivită. Nu trebuie încercată mai întâi.',
    newerDraftHint:
      'Publică această versiune atunci când formularea este potrivită. Nu trebuie încercată mai întâi.',
    publish: 'Publică',
    requestPublication: 'Solicită publicarea',
    publishing: 'Se publică…',
    published: 'Publicată pentru compania ta.',
    requested: 'Trimisă spre aprobare. Un editor verifică această versiune în Inboxul său.',
    publishFailed: 'Această versiune nu a putut fi publicată. Încearcă din nou.',
    companyFile: 'Fișierul companiei',
    discussPassage: 'Discută acest pasaj',
    contentLabel: 'Conținutul competenței',
    unpublish: 'Anulează publicarea versiunii companiei',
    unpublishQuestion:
      'Anulezi publicarea versiunii companiei? Colegii nu vor mai avea acces la instrucțiunile publicate, iar chat-urile și agenții nu le vor mai urma.',
    confirmUnpublish: 'Confirmă anularea publicării',
    unpublishing: 'Se anulează publicarea…',
    cancel: 'Anulează',
    unpublished: 'Publicarea versiunii companiei a fost anulată. Ciornele nu au fost modificate.',
    unpublishFailed: 'Publicarea acestei competențe nu a putut fi anulată.',
    newSkillTitle: 'Ce ar trebui să învețe această competență?',
    newSkillBody:
      'Descrie cum se face munca aici. Poneva redactează instrucțiunile în această parte, pe baza cunoștințelor companiei.',
    followedOne: '{count} competență urmărită',
    followedMany: 'Au fost urmate {count} competențe',
    followedSubtitle: 'Citită înainte de acest răspuns',
    howFound: 'Găsită pentru „{need}”',
    howFoundPlain: 'Găsită pentru această solicitare',
    howPicked: 'Selectată pentru acest chat',
    howAgent: 'Urmată întotdeauna de acest agent',
    howRead: 'Citită pentru acest răspuns',
    openFollowed: 'Deschide {name}, versiunea {revision}',
    followedEyebrow: 'Competență · Versiunea {revision}',
    followedContext: 'Urmată pentru acest răspuns · Versiunea {revision}',
    followedRecordNote:
      'Aceasta consemnează ce a primit Poneva înainte de a scrie acest răspuns, nu verifică dacă fiecare pas a fost urmat.',
    openInSkills: 'Deschide în Competențe',
    newerVersion: 'Există o versiune mai nouă (versiunea {revision}).',
    viewNewer: 'Vezi versiunea {revision}',
    versionUnavailable:
      'Versiunea {revision} nu mai este disponibilă pentru tine. Aceasta este versiunea actuală.',
    versionLoadFailed:
      'Versiunea {revision} nu a putut fi încărcată. Încearcă din nou pentru a vedea instrucțiunile folosite pentru acest răspuns.',
    retryVersion: 'Încearcă din nou',
    lookedFor: 'Căutat',
    searched: 'S-au căutat',
    searchedSkills: 'Competențe ale companiei, după linia „Utilizați când”',
    found: 'Găsite',
    bestFit: 'Cea mai potrivită',
    noneFound: 'Nicio competență nu corespunde acestei căutări.',
    skillLabel: 'Competență',
    versionLabel: 'Versiunea {revision}',
  },
  sk: {
    pageLead:
      'Ako sa tu odvádza kvalitná práca. Poneva nájde Zručnosť, ktorá sa hodí, v ľubovoľnom chate alebo agentovi, a použije ju spolu so znalosťami vašej spoločnosti.',
    search: 'Nájsť Zručnosti podľa významu…',
    pageNote:
      'Zručnosti môžete písať a upravovať v chate. Poneva používa zverejnené Zručnosti všade, kde sa hodia.',
    cardApplies: 'Použije sa, keď sa hodí',
    cardDraft: 'Koncept · Nezverejnené',
    cardNewDraft: 'Nový koncept',
    cardVersion: 'v{revision}',
    emptyTitle: 'Začnite niečím, čo robíte často.',
    emptyBody:
      'Povedzte Poneve, ako sa tu vykonáva určitý druh práce. Spolu s vami vytvorí pokyny na základe znalostí vašej spoločnosti a po zverejnení sa nimi bude riadiť každý chat a agent.',
    emptyAction: 'Vytvoriť prvú Zručnosť',
    draftCard: 'Koncept {revision} · Otvoriť pokyny',
    versionPublished: 'Verzia {revision} · Zverejnené',
    versionDraft: 'Verzia {revision} · Koncept',
    versionEarlier: 'Verzia {revision} · Staršia',
    publishedVersion: 'Verzia {revision} je zverejnená verzia.',
    useInChat: 'Použiť v chate',
    editInChat: 'Upraviť v chate',
    editLatest: 'Upraviť najnovšiu v chate',
    applies: 'Použije sa',
    appliesWhenItFits: 'Keď sa hodí',
    appliesHint:
      'Poneva ju nájde podľa riadku Použiť, keď v ľubovoľnom chate alebo spustení agenta.',
    filesRow: 'Súbory',
    tabInstructions: 'Pokyny',
    tabFollowedIn: 'Použitá v',
    tabVersions: 'Verzie',
    followedInAnswersOne: 'Použitá v {count} odpovedi tento týždeň.',
    followedInAnswersOther: 'Použitá v {count} odpovediach tento týždeň.',
    followedInEmpty:
      'Zatiaľ nebola použitá. Keď túto Zručnosť použije chat alebo spustenie agenta, zobrazí sa tu.',
    followedInFailed: 'Nepodarilo sa načítať, kde bola táto Zručnosť použitá.',
    versionsCurrent: 'Aktuálna',
    versionsView: 'Zobraziť',
    restore: 'Obnoviť ako nový koncept',
    restored: 'Staršie pokyny boli obnovené ako nový koncept.',
    restoreFailed: 'Túto verziu sa nepodarilo obnoviť.',
    publishedFor: 'Zverejnené pre {company}.',
    editStaysDraft: 'Zmeny, ktoré vykonáte v chate, zostanú konceptom, kým ich nezverejníte.',
    draftOnlyYou: 'Tento koncept vidíte iba vy.',
    draftShared: 'Tento koncept vidíte iba vy a ľudia, s ktorými je zdieľaný.',
    publishHint: 'Zverejnite ho, keď bude znenie správne. Najprv ho netreba skúšať.',
    newerDraftHint: 'Zverejnite túto verziu, keď bude znenie správne. Najprv ju netreba skúšať.',
    publish: 'Zverejniť',
    requestPublication: 'Požiadať o zverejnenie',
    publishing: 'Zverejňuje sa…',
    published: 'Zverejnené pre vašu spoločnosť.',
    requested:
      'Odoslané na schválenie. Vydavateľ skontroluje túto verziu vo svojej doručenej pošte.',
    publishFailed: 'Túto verziu sa nepodarilo zverejniť. Skúste to znova.',
    companyFile: 'Firemný súbor',
    discussPassage: 'Diskutovať o tejto pasáži',
    contentLabel: 'Obsah Zručnosti',
    unpublish: 'Zrušiť zverejnenie firemnej verzie',
    unpublishQuestion:
      'Zrušiť zverejnenie firemnej verzie? Kolegovia stratia prístup k jej zverejneným pokynom a chaty a agenti sa nimi prestanú riadiť.',
    confirmUnpublish: 'Potvrdiť zrušenie zverejnenia',
    unpublishing: 'Zverejnenie sa ruší…',
    cancel: 'Zrušiť',
    unpublished: 'Zverejnenie firemnej verzie bolo zrušené. Koncepty sa nezmenili.',
    unpublishFailed: 'Zverejnenie tejto Zručnosti sa nepodarilo zrušiť.',
    newSkillTitle: 'Čo má táto Zručnosť naučiť?',
    newSkillBody:
      'Opíšte, ako sa tu vykonáva práca. Poneva na tejto strane vytvorí koncept pokynov na základe znalostí vašej spoločnosti.',
    followedOne: 'Použitá Zručnosť: {count}',
    followedMany: 'Použité Zručnosti: {count}',
    followedSubtitle: 'Prečítané pred touto odpoveďou',
    howFound: 'Nájdená pre „{need}“',
    howFoundPlain: 'Nájdená pre túto požiadavku',
    howPicked: 'Vybraná pre tento chat',
    howAgent: 'Tento agent ju používa vždy',
    howRead: 'Prečítaná pre túto odpoveď',
    openFollowed: 'Otvoriť {name}, verzia {revision}',
    followedEyebrow: 'Zručnosť · Verzia {revision}',
    followedContext: 'Použitá pre túto odpoveď · Verzia {revision}',
    followedRecordNote:
      'Toto zaznamenáva, čo Poneva dostala pred vytvorením tejto odpovede, nie kontrolu, či bol dodržaný každý krok.',
    openInSkills: 'Otvoriť v Zručnostiach',
    newerVersion: 'Existuje novšia verzia (verzia {revision}).',
    viewNewer: 'Zobraziť verziu {revision}',
    versionUnavailable: 'Verzia {revision} už pre vás nie je dostupná. Toto je aktuálna verzia.',
    versionLoadFailed:
      'Nepodarilo sa načítať verziu {revision}. Skúste to znova a zobrazte pokyny použité pre túto odpoveď.',
    retryVersion: 'Skúsiť znova',
    lookedFor: 'Hľadané',
    searched: 'Vyhľadané',
    searchedSkills: 'Firemné zručnosti podľa riadku „Použiť, keď“',
    found: 'Nájdené',
    bestFit: 'Najlepšia zhoda',
    noneFound: 'Vyhľadávaniu nezodpovedá žiadna zručnosť.',
    skillLabel: 'Zručnosť',
    versionLabel: 'Verzia {revision}',
  },
  sl: {
    pageLead:
      'Tako se tukaj opravi dobro delo. Poneva v katerem koli klepetu ali agentu poišče ustrezno veščino in jo uporablja skupaj z znanjem vašega podjetja.',
    search: 'Poiščite veščine po pomenu…',
    pageNote:
      'Veščine pišite in spreminjajte v klepetu. Poneva uporablja objavljene veščine povsod, kjer so ustrezne.',
    cardApplies: 'Uporabi se, ko je ustrezna',
    cardDraft: 'Osnutek · Ni objavljeno',
    cardNewDraft: 'Nov osnutek',
    cardVersion: 'v{revision}',
    emptyTitle: 'Začnite z nečim, kar pogosto počnete.',
    emptyBody:
      'Povejte Ponevi, kako se tukaj opravlja določena vrsta dela. Navodila bo skupaj z vami zapisala na podlagi znanja vašega podjetja, vsi klepeti in agenti pa jim bodo sledili, ko jih objavite.',
    emptyAction: 'Ustvari svojo prvo veščino',
    draftCard: 'Osnutek {revision} · Odpri navodila',
    versionPublished: 'Različica {revision} · Objavljeno',
    versionDraft: 'Različica {revision} · Osnutek',
    versionEarlier: 'Različica {revision} · Prejšnja',
    publishedVersion: 'Različica {revision} je objavljena različica.',
    useInChat: 'Uporabi v klepetu',
    editInChat: 'Uredi v klepetu',
    editLatest: 'Uredi najnovejšo v klepetu',
    applies: 'Uporabi se',
    appliesWhenItFits: 'Ko je ustrezna',
    appliesHint:
      'Poneva jo poišče glede na vrstico »Uporabi, ko«, v katerem koli klepetu ali izvajanju agenta.',
    filesRow: 'Datoteke',
    tabInstructions: 'Navodila',
    tabFollowedIn: 'Uporabljeno v',
    tabVersions: 'Različice',
    followedInAnswersOne: 'Uporabljeno v {count} odgovoru ta teden.',
    followedInAnswersOther: 'Uporabljeno v {count} odgovorih ta teden.',
    followedInEmpty:
      'Še ni uporabljeno. Ko klepet ali izvajanje agenta uporabi to veščino, bo prikazano tukaj.',
    followedInFailed:
      'Podatkov o tem, kje je bila ta veščina uporabljena, ni bilo mogoče naložiti.',
    versionsCurrent: 'Trenutna',
    versionsView: 'Ogled',
    restore: 'Obnovi kot nov osnutek',
    restored: 'Prejšnja navodila so bila obnovljena kot nov osnutek.',
    restoreFailed: 'Te različice ni bilo mogoče obnoviti.',
    publishedFor: 'Objavljeno za {company}.',
    editStaysDraft:
      'Spremembe, ki jih naredite v klepetu, ostanejo osnutek, dokler jih ne objavite.',
    draftOnlyYou: 'Ta osnutek lahko vidite samo vi.',
    draftShared: 'Ta osnutek lahko vidite samo vi in osebe, s katerimi je v skupni rabi.',
    publishHint: 'Objavite, ko je vsebina ustrezna. Najprej vam ni treba ničesar preizkusiti.',
    newerDraftHint:
      'To različico objavite, ko je vsebina ustrezna. Najprej vam ni treba ničesar preizkusiti.',
    publish: 'Objavi',
    requestPublication: 'Zahtevaj objavo',
    publishing: 'Objavljanje…',
    published: 'Objavljeno za vaše podjetje.',
    requested: 'Poslano v odobritev. Objavitelj bo to različico pregledal v mapi Prejeto.',
    publishFailed: 'Te različice ni bilo mogoče objaviti. Poskusite znova.',
    companyFile: 'Datoteka podjetja',
    discussPassage: 'Razpravljaj o tem odlomku',
    contentLabel: 'Vsebina veščine',
    unpublish: 'Prekliči objavo različice podjetja',
    unpublishQuestion:
      'Želite preklicati objavo različice podjetja? Sodelavci ne bodo več imeli dostopa do objavljenih navodil, klepeti in agenti pa jim ne bodo več sledili.',
    confirmUnpublish: 'Potrdi preklic objave',
    unpublishing: 'Preklic objave…',
    cancel: 'Prekliči',
    unpublished: 'Objava različice podjetja je preklicana. Osnutki niso spremenjeni.',
    unpublishFailed: 'Objave te veščine ni bilo mogoče preklicati.',
    newSkillTitle: 'Kaj naj ta veščina nauči?',
    newSkillBody:
      'Opišite, kako se tukaj opravlja delo. Poneva bo na tej strani pripravila navodila na podlagi znanja vašega podjetja.',
    followedOne: 'Uporabljena veščina: {count}',
    followedMany: 'Uporabljene veščine: {count}',
    followedSubtitle: 'Prebrano pred tem odgovorom',
    howFound: 'Najdeno za »{need}«',
    howFoundPlain: 'Najdeno za to zahtevo',
    howPicked: 'Izbrano za ta klepet',
    howAgent: 'Ta agent jo vedno uporablja',
    howRead: 'Prebrano za ta odgovor',
    openFollowed: 'Odpri {name}, različico {revision}',
    followedEyebrow: 'Veščina · Različica {revision}',
    followedContext: 'Uporabljeno za ta odgovor · Različica {revision}',
    followedRecordNote:
      'To beleži, kaj je Poneva prejela pred pisanjem tega odgovora, ne pa preverjanja, ali je bil upoštevan vsak korak.',
    openInSkills: 'Odpri v Veščinah',
    newerVersion: 'Na voljo je novejša različica (različica {revision}).',
    viewNewer: 'Ogled različice {revision}',
    versionUnavailable: 'Različica {revision} vam ni več na voljo. To je trenutna različica.',
    versionLoadFailed:
      'Različice {revision} ni bilo mogoče naložiti. Poskusite znova, da si ogledate navodila, uporabljena za ta odgovor.',
    retryVersion: 'Poskusi znova',
    lookedFor: 'Iskanje',
    searched: 'Iskano',
    searchedSkills: 'Veščine podjetja po vrstici »Uporabi, ko«',
    found: 'Najdeno',
    bestFit: 'Najbolj ustreza',
    noneFound: 'Nobena veščina ne ustreza temu iskanju.',
    skillLabel: 'Veščina',
    versionLabel: 'Različica {revision}',
  },
  es: {
    pageLead:
      'Así es como se hace un buen trabajo aquí. Poneva encuentra la Habilidad adecuada en cualquier chat o agente y la sigue junto con los conocimientos de tu empresa.',
    search: 'Buscar Habilidades por significado…',
    pageNote:
      'Escribe y cambia Habilidades en el chat. Poneva sigue las Habilidades publicadas allí donde encajan.',
    cardApplies: 'Se aplica cuando encaja',
    cardDraft: 'Borrador · No publicada',
    cardNewDraft: 'Nuevo borrador',
    cardVersion: 'v{revision}',
    emptyTitle: 'Empieza por algo que hagas a menudo.',
    emptyBody:
      'Cuéntale a Poneva cómo se hace un tipo de trabajo aquí. Redactará contigo las instrucciones a partir de los conocimientos de tu empresa, y todos los chats y agentes las seguirán cuando las publiques.',
    emptyAction: 'Crea tu primera Habilidad',
    draftCard: 'Borrador {revision} · Abrir instrucciones',
    versionPublished: 'Versión {revision} · Publicada',
    versionDraft: 'Versión {revision} · Borrador',
    versionEarlier: 'Versión {revision} · Anterior',
    publishedVersion: 'La versión {revision} es la versión publicada.',
    useInChat: 'Usar en el chat',
    editInChat: 'Editar en el chat',
    editLatest: 'Editar la última en el chat',
    applies: 'Se aplica',
    appliesWhenItFits: 'Cuando encaja',
    appliesHint:
      'Poneva la encuentra por su línea «Usar cuando» en cualquier chat o ejecución de agente.',
    filesRow: 'Archivos',
    tabInstructions: 'Instrucciones',
    tabFollowedIn: 'Aplicada en',
    tabVersions: 'Versiones',
    followedInAnswersOne: 'Aplicada en {count} respuesta esta semana.',
    followedInAnswersOther: 'Aplicada en {count} respuestas esta semana.',
    followedInEmpty:
      'Todavía no se ha aplicado. Cuando un chat o una ejecución de agente siga esta Habilidad, aparecerá aquí.',
    followedInFailed: 'No se han podido cargar los lugares donde se aplicó esta Habilidad.',
    versionsCurrent: 'Actual',
    versionsView: 'Ver',
    restore: 'Restaurar como nuevo borrador',
    restored: 'Las instrucciones anteriores se han restaurado como un nuevo borrador.',
    restoreFailed: 'No se ha podido restaurar esta versión.',
    publishedFor: 'Publicada para {company}.',
    editStaysDraft:
      'Los cambios que hagas en el chat seguirán siendo un borrador hasta que los publiques.',
    draftOnlyYou: 'Solo tú puedes ver este borrador.',
    draftShared: 'Solo tú y las personas con las que se comparte pueden ver este borrador.',
    publishHint: 'Publícala cuando el texto esté bien. No es necesario probar nada antes.',
    newerDraftHint:
      'Publica esta versión cuando el texto esté bien. No es necesario probar nada antes.',
    publish: 'Publicar',
    requestPublication: 'Solicitar publicación',
    publishing: 'Publicando…',
    published: 'Publicada para tu empresa.',
    requested:
      'Enviada para su aprobación. Un publicador revisará esta versión en su Bandeja de entrada.',
    publishFailed: 'No se ha podido publicar esta versión. Inténtalo de nuevo.',
    companyFile: 'Archivo de la empresa',
    discussPassage: 'Comentar este pasaje',
    contentLabel: 'Contenido de la Habilidad',
    unpublish: 'Retirar versión de la empresa',
    unpublishQuestion:
      '¿Retirar la versión de la empresa? Tus compañeros perderán el acceso a sus instrucciones publicadas y los chats y agentes dejarán de seguirla.',
    confirmUnpublish: 'Confirmar retirada',
    unpublishing: 'Retirando…',
    cancel: 'Cancelar',
    unpublished: 'Versión de la empresa retirada. Los borradores no han cambiado.',
    unpublishFailed: 'No se ha podido retirar esta Habilidad.',
    newSkillTitle: '¿Qué debería enseñar esta Habilidad?',
    newSkillBody:
      'Describe cómo se hace el trabajo aquí. Poneva redactará las instrucciones en este espacio a partir de los conocimientos de tu empresa.',
    followedOne: 'Habilidad seguida: {count}',
    followedMany: 'Habilidades seguidas: {count}',
    followedSubtitle: 'Leída antes de esta respuesta',
    howFound: 'Encontrada para «{need}»',
    howFoundPlain: 'Encontrada para esta solicitud',
    howPicked: 'Elegida para este chat',
    howAgent: 'Este agente siempre la sigue',
    howRead: 'Leída para esta respuesta',
    openFollowed: 'Abrir {name}, versión {revision}',
    followedEyebrow: 'Habilidad · Versión {revision}',
    followedContext: 'Seguida para esta respuesta · Versión {revision}',
    followedRecordNote:
      'Esto registra lo que recibió Poneva antes de redactar esta respuesta, no comprueba que se haya seguido cada paso.',
    openInSkills: 'Abrir en Habilidades',
    newerVersion: 'Hay una versión más reciente (versión {revision}).',
    viewNewer: 'Ver la versión {revision}',
    versionUnavailable: 'Ya no tienes disponible la versión {revision}. Esta es la versión actual.',
    versionLoadFailed:
      'No se ha podido cargar la versión {revision}. Inténtalo de nuevo para ver las instrucciones usadas para esta respuesta.',
    retryVersion: 'Intentar de nuevo',
    lookedFor: 'Buscado',
    searched: 'Búsqueda',
    searchedSkills: 'Habilidades de la empresa, según su línea «Cuándo usar»',
    found: 'Encontrado',
    bestFit: 'Mejor coincidencia',
    noneFound: 'Ninguna habilidad coincide con esta búsqueda.',
    skillLabel: 'Habilidad',
    versionLabel: 'Versión {revision}',
  },
  sv: {
    pageLead:
      'Så görs bra arbete här. Poneva hittar skillen som passar, i alla chattar och agenter, och följer den ovanpå er kunskap.',
    search: 'Hitta skills efter betydelse…',
    pageNote: 'Skriv och ändra skills i chatten. Poneva följer publicerade skills där de passar.',
    cardApplies: 'Används när den passar',
    cardDraft: 'Utkast · Inte publicerad',
    cardNewDraft: 'Nytt utkast',
    cardVersion: 'v{revision}',
    emptyTitle: 'Börja med något ni gör ofta.',
    emptyBody:
      'Berätta för Poneva hur en sorts arbete görs här. Poneva skriver instruktionerna med dig, ovanpå er kunskap, och alla chattar och agenter följer dem när du har publicerat.',
    emptyAction: 'Skapa din första skill',
    draftCard: 'Utkast {revision} · Öppna instruktionerna',
    versionPublished: 'Version {revision} · Publicerad',
    versionDraft: 'Version {revision} · Utkast',
    versionEarlier: 'Version {revision} · Tidigare',
    publishedVersion: 'Version {revision} är den publicerade versionen.',
    useInChat: 'Använd i chatten',
    editInChat: 'Ändra i chatten',
    editLatest: 'Ändra senaste i chatten',
    applies: 'Används',
    appliesWhenItFits: 'När den passar',
    appliesHint: 'Poneva hittar den via raden Används när, i alla chattar och agentkörningar.',
    filesRow: 'Filer',
    tabInstructions: 'Instruktioner',
    tabFollowedIn: 'Följd i',
    tabVersions: 'Versioner',
    followedInAnswersOne: 'Följd i {count} svar den här veckan.',
    followedInAnswersOther: 'Följd i {count} svar den här veckan.',
    followedInEmpty:
      'Inte följd ännu. När en chatt eller en agentkörning följer skillen visas det här.',
    followedInFailed: 'Det gick inte att läsa in var skillen har följts.',
    versionsCurrent: 'Aktuell',
    versionsView: 'Visa',
    restore: 'Återställ som nytt utkast',
    restored: 'Tidigare instruktioner återställda som nytt utkast.',
    restoreFailed: 'Den här versionen kunde inte återställas.',
    publishedFor: 'Publicerad för {company}.',
    editStaysDraft: 'Det du ändrar i chatten förblir ett utkast tills du publicerar det.',
    draftOnlyYou: 'Bara du kan se det här utkastet.',
    draftShared: 'Bara du och de som det delas med kan se det här utkastet.',
    publishHint: 'Publicera när den läser rätt. Inget behöver provas först.',
    newerDraftHint: 'Publicera den här versionen när den läser rätt. Inget behöver provas först.',
    publish: 'Publicera',
    requestPublication: 'Begär publicering',
    publishing: 'Publicerar…',
    published: 'Publicerad för ert företag.',
    requested: 'Skickad för godkännande. En publicerare granskar versionen i sin inkorg.',
    publishFailed: 'Versionen kunde inte publiceras. Försök igen.',
    companyFile: 'Företagsfil',
    discussPassage: 'Diskutera stycket',
    contentLabel: 'Skillens innehåll',
    unpublish: 'Avpublicera företagsversionen',
    unpublishQuestion:
      'Avpublicera företagsversionen? Kollegor förlorar åtkomst till de publicerade instruktionerna, och chattar och agenter slutar följa den.',
    confirmUnpublish: 'Bekräfta avpublicering',
    unpublishing: 'Avpublicerar…',
    cancel: 'Avbryt',
    unpublished: 'Företagsversionen är avpublicerad. Utkasten är oförändrade.',
    unpublishFailed: 'Det gick inte att avpublicera skillen.',
    newSkillTitle: 'Vad ska den här skillen lära ut?',
    newSkillBody:
      'Beskriv hur arbetet görs här. Poneva skriver instruktionerna på den här sidan, utifrån er kunskap.',
    followedOne: 'Följde {count} skill',
    followedMany: 'Följde {count} skills',
    followedSubtitle: 'Lästa före det här svaret',
    howFound: 'Hittad för ”{need}”',
    howFoundPlain: 'Hittad för den här frågan',
    howPicked: 'Vald för den här chatten',
    howAgent: 'Följs alltid av den här agenten',
    howRead: 'Läst för det här svaret',
    openFollowed: 'Öppna {name}, version {revision}',
    followedEyebrow: 'Färdighet · Version {revision}',
    followedContext: 'Följd för det här svaret · Version {revision}',
    followedRecordNote:
      'Detta visar vad Poneva fick innan svaret skrevs, inte att varje steg följdes.',
    openInSkills: 'Öppna i Skills',
    newerVersion: 'Det finns en nyare version (version {revision}).',
    viewNewer: 'Visa version {revision}',
    versionUnavailable:
      'Version {revision} är inte längre tillgänglig för dig. Det här är den aktuella versionen.',
    versionLoadFailed:
      'Kunde inte läsa version {revision}. Försök igen för att se instruktionerna som användes för det här svaret.',
    retryVersion: 'Försök igen',
    lookedFor: 'Letade efter',
    searched: 'Sökte i',
    searchedSkills: 'Företagets skills, via raden Används när',
    found: 'Hittade',
    bestFit: 'Passar bäst',
    noneFound: 'Ingen skill matchade sökningen.',
    skillLabel: 'Färdighet',
    versionLabel: 'Version {revision}',
  },
};
export default copy;
